import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./editor.css";
import Design from "./components/Design/Design";
import { fabric } from "fabric";
import QRious from "qrious";
import Text from "./components/Text/Text";
import EditorOps from "./EditorOps";
import { refreshClockWidgets } from "./utils/refreshClockWidgets";
import {
  applyClockWidgetStyle,
  mergeClockObjStyle,
} from "./utils/clockWidgetStyle";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  CloudSun,
  Image as ImageIcon,
  ImageOff,
  Layers,
  List,
  LogIn,
  Maximize2,
  Minus,
  MonitorPlay,
  PanelLeft,
  PanelRight,
  Plus,
  QrCode,
  Rss,
  Save,
  Search,
  ThermometerSun,
  Trash2,
  Type,
  UserPlus,
  Video as VideoIcon,
  Wallpaper,
} from "lucide-react";
import Time from "./components/Time/Time";
import DateWidget from "./components/Date/DateWidget";
import QRCode from "./components/QRCode/QRCode";
import {
  _addFromCanvaTemplate,
  _editToCanvaTemplate,
  contentFlagOff,
  getContent,
} from "../Redux/Actions/contentAction";
import { getPlaylist } from "../Redux/Actions/playlistAction";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { publicAssetUrl, resolveMediaUrl } from "../utils/resolveMediaUrl";
import { useContextUserId } from "../hooks/useSupportContext";
import { ReactComponent as CDesign } from "../Assets/icons/design.svg";
import RssComponent from "./components/Rss/RssComponent";
import Weather from "./components/weather/Weather";
import TextStrip from "./components/textstrip/TextStrip";
import Temperature from "./components/Temprature/Temperature";
import DirectoryRegister from "./components/DirectoryRegister/DirectoryRegister";
import ClockWidgetPicker from "./components/Clock/ClockWidgetPicker";
import ClockWidgetStylePanel from "./components/Clock/ClockWidgetStylePanel";
import WeatherWidgetPicker from "./components/weather/WeatherWidgetPicker";
import WeatherWidgetStylePanel from "./components/weather/WeatherWidgetStylePanel";
import { refreshWeatherWidgets } from "./utils/refreshWeatherWidgets";
import { clearWeatherCache } from "./utils/weatherApi";
import {
  applyWeatherWidgetStyle,
  mergeWeatherObjStyle,
} from "./utils/weatherWidgetStyle";
import { Popover } from "@mui/material";
import { SketchPicker } from "react-color";
import { api } from "../api/api";
import { canEditCms } from "../utils/accessControl";

export default function DesignEditor() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const _folderId = searchParams.get("_folderId");
  const templateFromNav = useRef(location.state || null);
  /** Owner user id + folder for the layout being edited (customer) so saves do not use the logged-in admin id. */
  const layoutContentContextRef = useRef({
    ownerUserId: null,
    folderId: null,
  });
  const { _textProps, _fontColor, isChanged } = useSelector(
    (state) => state.Editor
  );

  const { user, isContentAdded, isContentUpdated, content: allContent, playlist: allPlaylists } = useSelector(
    (state) => ({
      user: state.User.user,
      isContentAdded: state.Content.isContentAdded,
      isContentUpdated: state.Content.isContentUpdated,
      content: state.Content.content,
      playlist: state.Playlist.playlist,
    }),
    shallowEqual
  );
  const dispatch = useDispatch();
  const canEdit = canEditCms(user);
  const contextUserId = useContextUserId();

  useEffect(() => {
    if (user?._id && !canEdit) {
      toast.error("View-only access. You cannot create or edit layouts.");
      navigate("/layout", { replace: true });
    }
  }, [user, canEdit, navigate]);
  const [layers, setLayers] = useState([]);
  const [objecKey, setObjectKey] = useState("default");
  const canvasRef = useRef(null);
  const [type, setType] = useState("Design");
  const [file, setFile] = useState("");
  const [leftTab, setLeftTab] = useState("media");
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 968
  );
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false);
  const [mobileRightOpen, setMobileRightOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sizeAnchorEl, setSizeAnchorEl] = useState(null);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [rsColorAnchorEl, setRsColorAnchorEl] = useState(null);
  const [registerBgAnchorEl, setRegisterBgAnchorEl] = useState(null);
  const [registerTextColorAnchorEl, setRegisterTextColorAnchorEl] = useState(null);
  // const [height, setHeight] = useState(1080);
  // const [width, setWidth] = useState(1920);
  const [pan, setPan] = useState(false);
  const [id, setId] = useState("");
  const [_textAlign, setTextAlign] = useState("");
  const [canvaProps, setCanvaProps] = useState({});
  const [textBoxProps, setTextBoxProps] = useState({});
  const [shapeProps, setShapeProps] = useState({});
  const [textTimeProps, setTextTimeProps] = useState({});
  const [textDateProps, setTextDateProps] = useState({});
  const [textStripProps, setTextStripProps] = useState({});
  const [weatherProps, setWeatherProps] = useState({});
  const [tempProps, setTempProps] = useState({});
  const [playlistProps, setPlaylistProps] = useState({});
  const [registerProps, setRegisterProps] = useState(null);
  const [clockWidgetProps, setClockWidgetProps] = useState({});
  const [weatherWidgetProps, setWeatherWidgetProps] = useState({});
  const [templateName, setTemplateName] = useState("");
  const [length, setLength] = useState({
    height: "",
    width: "",
  });
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [activeObj, setActiveObj] = useState({});
  const [canvaColor, setCanvaColor] = useState("white");
  const [zoom, setZoom] = useState(1);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [canvaId, setCanvaId] = useState("");
  const isDragging = useRef(false);
  const weatherRefreshTimerRef = useRef(null);
  const updateDirectoryRegisterGroupRef = useRef(null);
  /** True while we replace the register group on canvas — avoids object:added/modified reflow loops (stack overflow + runaway font scale). */
  const registerReplaceInProgressRef = useRef(false);
  const [speedValue, setSpeedValue] = useState("");
  const [stripText, setStripText] = useState("");
  const [stripDirection, setStripDirection] = useState("");
  const [height, setHeight] = useState(1080);
  const [width, setWidth] = useState(1920);
  /** Always matches latest width/height so delayed callbacks (e.g. fit after loadFromJSON) don't use stale values. */
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  useEffect(() => {
    widthRef.current = width;
    heightRef.current = height;
  }, [width, height]);
  const [orientation, setOrientation] = useState("landscape");
  const [canvasLength, setCanvasLength] = useState({
    height: 1080,
    width: 1920,
  });
  const aspectRatio = useRef(width / height);
  /** Skip width/height useEffect while loading a template (avoids race with loadFromJSON). */
  const isLoadingTemplateRef = useRef(false);
  /** Fabric canvas.backgroundImage is set (full-cover layout background). */
  const [hasCanvasBackground, setHasCanvasBackground] = useState(false);
  const containerRef = useRef(null);
  const [scrollStrip, setScrollStrip] = useState({
    duration: "",
    text: "",
    direction: "",
  });
  const [qrProps, setQrProps] = useState({});
  const addCanvasImage = (img, onFailMessage) => {
    if (!img || !canvasRef.current) {
      if (onFailMessage) toast.error(onFailMessage);
      return false;
    }
    setLayers((prev) => [...prev, img]);
    canvasRef.current.add(img);
    canvasRef.current.requestRenderAll();
    return true;
  };
  // Custom properties that must be preserved in canvas JSON serialization.
  // Passed to toJSON() so fabric.js includes them via its built-in propertiesToInclude mechanism.
  const CUSTOM_PROPS = ['id', 'name', 'eleType', 'url', 'linkUrl', '_name', 'text', 'fontSize', 'contents', 'playlistId', 'weatherObj', 'scaletype', 'tempObj', 'zoom', 'scrollstrip', 'registerObj', 'clockObj'];

  const filteredImages = useMemo(() => allContent?.filter(c => c.type === 'image' && (searchQuery === '' || c.name?.toLowerCase().includes(searchQuery.toLowerCase()))) || [], [allContent, searchQuery]);
  const filteredVideos = useMemo(() => allContent?.filter(c => c.type === 'video' && (searchQuery === '' || c.name?.toLowerCase().includes(searchQuery.toLowerCase()))) || [], [allContent, searchQuery]);
  const filteredPlaylists = useMemo(() => allPlaylists?.filter(p => searchQuery === '' || p.name?.toLowerCase().includes(searchQuery.toLowerCase())) || [], [allPlaylists, searchQuery]);

  useEffect(() => {
    if (!contextUserId) return;

    // Editor relies on redux content/playlist state; fetch on entry to avoid empty media panel.
    dispatch(getContent(contextUserId, { page: 1, limit: 200 }));
    dispatch(getPlaylist(contextUserId, { page: 1, limit: 200 }));
  }, [dispatch, contextUserId]);

  useEffect(() => {
    const container = document.querySelector("#outer-container");
    const canvas = new fabric.Canvas("canvas", {
      name: "canva-main",
      height: height,
      width: width,
      backgroundColor: "white",
      // borderRadius:'10px',
      selection: true,
      stopContextMenu: true,
      fireRightClick: true,
      preserveObjectStacking: true,
      enableRetinaScaling: true,
    });
    // canvas.setHeight(container.clientHeight - 150);
    // canvas.setWidth(container.clientWidth - 150);

    // setDimention({
    //   height: container.clientHeight - 150,
    //   width: container.clientWidth - 150,
    // });

    setCanvaProps({
      name: "canva-main",
      // height: 500,
      // width: 800,
      backgroundColor: "white",
      selection: true,
    });

    canvasRef.current = canvas;
    // Initialize the toObject override so custom properties (eleType, contents, url, etc.)
    // are always included in toJSON() — even when loading/editing existing templates
    // without adding new elements.
    new EditorOps();
    saveHistory(canvas);
    canvas.requestRenderAll();

    refreshClockWidgets(canvas);
    refreshWeatherWidgets(canvas);
    const clockTickMs = 100;
    const clockWidgetsInterval = setInterval(() => {
      refreshClockWidgets(canvas);
    }, clockTickMs);
    const weatherRefreshMs = 5 * 60 * 1000;
    const weatherWidgetsInterval = setInterval(() => {
      refreshWeatherWidgets(canvas);
    }, weatherRefreshMs);

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(fitCanvasToScreen, 50);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      clearInterval(clockWidgetsInterval);
      clearInterval(weatherWidgetsInterval);
      resizeObserver.disconnect();
      canvas.dispose();
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 968px)");
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) {
        setMobileLeftOpen(false);
        setMobileRightOpen(false);
      }
    };
    onChange(mq);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const t = setTimeout(fitCanvasToScreen, 80);
    return () => clearTimeout(t);
  }, [mobileLeftOpen, mobileRightOpen, isMobile]);

  const fitCanvasToScreen = () => {
    if (!canvasRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const narrow =
      container.clientWidth < 520 || window.innerWidth <= 968;
    const padding = narrow ? 12 : 40;
    const w = widthRef.current;
    const h = heightRef.current;
    const availableWidth = container.clientWidth - padding;
    const availableHeight = container.clientHeight - padding;
    const scaleX = availableWidth / w;
    const scaleY = availableHeight / h;
    const fitZoom = Math.min(scaleX, scaleY, 1);
    setZoom(fitZoom);
    canvasRef.current.setZoom(fitZoom);
    canvasRef.current.setDimensions({
      width: w * fitZoom,
      height: h * fitZoom,
    });
  };

  useEffect(() => {
    if (!canvasRef.current || isLoadingTemplateRef.current) return;
    canvasRef.current.setDimensions({ width, height });
    canvasRef.current.setZoom(1);
    canvasRef.current.renderAll();
    setTimeout(fitCanvasToScreen, 50);
  }, [width, height]);

  useEffect(() => {
    if (canvasRef.current && templateFromNav.current) {
      const {
        templateId,
        canva,
        name,
        layoutOwnerUserId,
        layoutFolderId,
      } = templateFromNav.current;
      templateFromNav.current = null;
      if (canva && templateId) {
        setTimeout(() => {
          onLoadTemplateFromDb(canva, templateId, name || "", {
            layoutOwnerUserId,
            layoutFolderId: layoutFolderId ?? _folderId,
          });
        }, 300);
      }
    }
  }, [canvasRef.current]);

  useEffect(() => {
    if (canvasRef.current) {
      const handleObjectAddedOrModified = (e) => {
        // Fabric often fires object:modified with only { target } (no e.action). If we only
        // listened for e.action === 'scale', resize never rebaked layout and text kept the
        // old group scale while slider values (title/row size) no longer matched the canvas.
        if (
          e?.target?.eleType === "directory-register" &&
          !registerReplaceInProgressRef.current
        ) {
          const t = e.target;
          const reg = t.registerObj;
          const gid = t.id;
          const effW = Math.max(1, (t.width || 348) * Math.abs(t.scaleX || 1));
          const effH = Math.max(1, (t.height || 500) * Math.abs(t.scaleY || 1));
          const last = t._registerLayoutSize;
          const scaleDirty =
            Math.abs((t.scaleX || 1) - 1) > 0.001 ||
            Math.abs((t.scaleY || 1) - 1) > 0.001;
          // Compare Fabric-measured bounds to the last Fabric snapshot only. Using canonical
          // _layoutW here caused effW !== last.w almost always → endless reflow → stack overflow
          // and layoutScale climbing on every slider tick.
          const sizeDirty =
            !!last &&
            (Math.abs(last.w - effW) > 0.5 || Math.abs(last.h - effH) > 0.5);
          const a = e.action ?? e.transform?.action;
          const actionIsResize =
            a === "scale" ||
            a === "scaleX" ||
            a === "scaleY" ||
            a === "resizing" ||
            (typeof a === "string" && a.indexOf("scale") === 0);

          if (
            reg &&
            gid &&
            updateDirectoryRegisterGroupRef.current &&
            (actionIsResize || scaleDirty)
          ) {
            updateDirectoryRegisterGroupRef.current(gid, reg);
            canvasRef.current.requestRenderAll();
            if (!isDragging.current) {
              saveHistory(canvasRef.current);
            }
            return;
          }
        }
        // Preserve eleType and other custom properties when objects are modified
        if (e && e.target) {
          // Get current objects from canvas to find existing object (before modification)
          // Note: e.target is the modified object, we need to check if custom properties are missing
          const currentObjects = canvasRef.current.getObjects();
          const existingObject = currentObjects.find(l => l.id === e.target.id && l !== e.target);
          
          // If we can't find it in other objects, the object itself should have the properties
          // But if eleType is missing, we need to preserve it from the object's state before modification
          // Since fabric.js should preserve properties, we only restore if they're actually missing
          if (!e.target.eleType && existingObject && existingObject.eleType) {
            e.target.eleType = existingObject.eleType;
          }
          if (e.target.url === undefined && existingObject && existingObject.url !== undefined) {
            e.target.url = existingObject.url;
          }
          if (e.target._name === undefined && existingObject && existingObject._name !== undefined) {
            e.target._name = existingObject._name;
          }
          if (e.target.linkUrl === undefined && existingObject && existingObject.linkUrl !== undefined) {
            e.target.linkUrl = existingObject.linkUrl;
          }
          if (e.target.registerObj === undefined && existingObject && existingObject.registerObj !== undefined) {
            e.target.registerObj = existingObject.registerObj;
          }
          if (e.target.weatherObj === undefined && existingObject && existingObject.weatherObj !== undefined) {
            e.target.weatherObj = existingObject.weatherObj;
          }
          if (e.target.tempObj === undefined && existingObject && existingObject.tempObj !== undefined) {
            e.target.tempObj = existingObject.tempObj;
          }
          if (e.target.scaletype === undefined && existingObject && existingObject.scaletype !== undefined) {
            e.target.scaletype = existingObject.scaletype;
          }
          if (e.target.contents === undefined && existingObject && existingObject.contents !== undefined) {
            e.target.contents = existingObject.contents;
          }
          if (e.target.playlistId === undefined && existingObject && existingObject.playlistId !== undefined) {
            e.target.playlistId = existingObject.playlistId;
          }
          if (e.target.scrollstrip === undefined && existingObject && existingObject.scrollstrip !== undefined) {
            e.target.scrollstrip = existingObject.scrollstrip;
          }
          if (e.target.clockObj === undefined && existingObject && existingObject.clockObj !== undefined) {
            e.target.clockObj = existingObject.clockObj;
          }
          if (e.target.weatherObj === undefined && existingObject && existingObject.weatherObj !== undefined) {
            e.target.weatherObj = existingObject.weatherObj;
          }
        }
        canvasRef.current.requestRenderAll();
        // Only save to history if it's not part of dragging
        if (!isDragging.current) {
          saveHistory(canvasRef.current);
        }
      };

      const handleObjectMoving = () => {
        canvasRef.current.requestRenderAll();
        // Mark as dragging to avoid redundant saves
        isDragging.current = true;
      };

      const handleMouseUp = () => {
        canvasRef.current.requestRenderAll();
        // Save the final position of the object after dragging
        if (isDragging.current) {
          isDragging.current = false; // Reset dragging state
          saveHistory(canvasRef.current); // Save the final state
        }
      };

      canvasRef.current?.on("object:added", handleObjectAddedOrModified);
      canvasRef.current?.on("object:modified", handleObjectAddedOrModified);
      canvasRef.current?.on("object:moving", handleObjectMoving);
      canvasRef.current?.on("mouse:up", handleMouseUp);

      return () => {
        canvasRef.current?.off("object:added", handleObjectAddedOrModified);
        canvasRef.current?.off("object:modified", handleObjectAddedOrModified);
        canvasRef.current?.off("object:moving", handleObjectMoving);
        canvasRef.current?.off("mouse:up", handleMouseUp);
      };
    }
  }, [canvasRef.current]);

  const elementFunc = (e, layers) => {
    for (let ele of layers) {
      if (ele.id == e.target.id) {
        if (ele.eleType === "directory-register") {
          const effW = (ele.width || 0) * Math.abs(ele.scaleX || 1);
          const effH = (ele.height || 0) * Math.abs(ele.scaleY || 1);
          const ro = ele.registerObj || {};
          const canonW =
            typeof ro._layoutW === "number" && ro._layoutW > 0 ? ro._layoutW : effW;
          const canonH =
            typeof ro._layoutH === "number" && ro._layoutH > 0 ? ro._layoutH : effH;
          setRegisterProps({
            ...ro,
            left: ele.left,
            top: ele.top,
            width: canonW,
            height: canonH,
          });
          setId(e.target.id);
          setType("DirectoryRegister");
          break;
        }
        if (ele.eleType === "clock-widget") {
          const effW = (ele.width || 0) * Math.abs(ele.scaleX || 1);
          const effH = (ele.height || 0) * Math.abs(ele.scaleY || 1);
          setClockWidgetProps({
            ...(ele.clockObj || {}),
            variant: ele.clockObj?.variant || "gradient-night",
            style: mergeClockObjStyle(ele.clockObj),
            left: ele.left,
            top: ele.top,
            width: effW,
            height: effH,
            isSelected: true,
          });
          setId(e.target.id);
          setType("ClockWidget");
          break;
        }
        if (ele.eleType === "weather-widget") {
          const effW = (ele.width || 0) * Math.abs(ele.scaleX || 1);
          const effH = (ele.height || 0) * Math.abs(ele.scaleY || 1);
          setWeatherWidgetProps({
            ...(ele.weatherObj || {}),
            variant: ele.weatherObj?.variant || "glass-card",
            style: mergeWeatherObjStyle(ele.weatherObj),
            left: ele.left,
            top: ele.top,
            width: effW,
            height: effH,
            isSelected: true,
          });
          setId(e.target.id);
          setType("WeatherWidget");
          break;
        }
        console.log(ele);
        if (e.target.name == "Textbox") {
          if (ele.eleType == "text") {
            const hasShadow = ele.shadow && (ele.shadow.blur || ele.shadow.offsetX !== undefined || ele.shadow.offsetY !== undefined);
            setTextBoxProps((prev) => ({
              ...prev,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              fill: ele.fill,
              stroke: ele.stroke,
              textAlign: ele.textAlign,
              shadow: ele.shadow,
              isShadow: !!hasShadow,
              fontSize: ele.fontSize,
              fontWeight: ele.fontWeight || "normal",
              fontStyle: ele.fontStyle || "normal",
              underline: ele.underline || false,
              linethrough: ele.linethrough || false,
            }));
            setId(e.target?.id);
            setType("Text");
          }
          if (ele.eleType == "scrollstrip") {
            setTextStripProps((prev) => ({
              ...prev,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              text: ele.text,
              scrollstrip: ele?.scrollstrip ?? "",
              fill: ele.fill,
              stroke: ele.stroke,
              textAlign: ele.textAlign,
              shadow: ele.shadow,
              fontSize: ele.fontSize,
              isSelected: true,
            }));
            setId(e.target?.id);
            setType("TextStrip");
          }
          if (ele.eleType == "login" || ele.eleType == "register") {
            setId(e.target?.id);
            setType(ele.eleType === "login" ? "Login" : "Register");
          }
        }
        // if (e.target.name == "Shape" || e.target?.type == "rect") {
        //   // console.log(e);
        //   if (e.target.shapeType == "rect") {
        //     setShapeProps((prev) => ({
        //       ...prev,
        //       shapeType: e.target.shapeType,
        //       fill: ele.fill,
        //       height: ele.height,
        //       width: ele.width,
        //       stroke: ele.stroke,
        //       shadow: ele.shadow,
        //       radius: ele.rx ?? 0,
        //       strokeWidth: ele.strokeWidth,
        //     }));
        //   }
        //   if (
        //     e.target.shapeType == "circle" ||
        //     e.target.shapeType == "line" ||
        //     e.target.shapeType == "polygone" ||
        //     e.target.shapeType == "triangle" ||
        //     ["circle", "line", "polygone", "triangle"].includes(
        //       e.target?.type
        //     )
        //   ) {
        //     setShapeProps((prev) => ({
        //       ...prev,
        //       offsetX:ele?.left,
        //       offsetY:ele?.top,
        //       width:ele?.width * ele?.scaleX,
        //       height:ele?.height * ele?.scaleY,
        //       shapeType: e.target.shapeType,
        //       fill: ele.fill,
        //       height: ele.height,
        //       width: ele.width,
        //       stroke: ele.stroke,
        //       shadow: ele.shadow,
        //       strokeWidth: ele.strokeWidth,
        //     }));
        //   }
        //   setId(e.target?.id);
        //   setType("Shape");
        //   setObjectKey("Shapes");
        // }
        if (e.target.type == "image") {
          if (ele.eleType == "qr-code") {
            setQrProps((prev) => ({
              ...prev,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
            }));
            setId(e.target?.id);
            setType("QRCode");
          }
          if (ele.eleType == "weather") {
            setWeatherProps({
              ...ele.weatherObj,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              isSelected: true,
            });
            setId(e.target?.id);
            setType("Weather");
          }
          if (ele.eleType == "temp") {
            setTempProps({
              ...ele.tempObj,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              isSelected: true,
            });
            setId(e.target?.id);
            setType("Temprature");
          }
          if (["playlist", "nor-video", "nor-image"].includes(ele.eleType)) {
            setId(e.target.id);
            setType("ToolPlaylistOp");
            setPlaylistProps((prev) => ({
              ...prev,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              name: ele._name ?? "",
              type: ele.eleType,
              ...ele.scaletype,
            }));
          }
          if (ele.eleType == "analog-clock") {
            setTextTimeProps((prev) => ({
              ...prev,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              fill: ele.fill,
              stroke: ele.stroke,
              textAlign: ele.textAlign,
              shadow: ele.shadow,
              fontSize: ele.fontSize,
              isSelected: true,
            }));
            setId(e.target?.id);
            setType("Time");
          }
        }

        if (e.target.name == "Text" || e.target?.type == "text") {
          // console.log(ele);
          if (ele.eleType == "time") {
            setTextTimeProps((prev) => ({
              ...prev,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              fill: ele.fill,
              stroke: ele.stroke,
              textAlign: ele.textAlign,
              shadow: ele.shadow,
              fontSize: ele.fontSize,
              isSelected: true,
            }));
            setId(e.target?.id);
            setType("Time");
          }

          if (ele.eleType == "date") {
            setTextDateProps((prev) => ({
              ...prev,
              scaleX: ele?.scaleX,
              scaleY: ele?.scaleY,
              left: ele?.left,
              top: ele?.top,
              width: ele?.width,
              height: ele?.height,
              fill: ele.fill,
              stroke: ele.stroke,
              textAlign: ele.textAlign,
              shadow: ele.shadow,
              fontSize: ele.fontSize,
              isSelected: true,
            }));
            setId(e.target?.id);
            setType("Date");
          }

          // setObjectKey("TextProperty");
        }
        break;
      }
    }
  };

  useEffect(() => {
    canvasRef.current.on("object:moving", (e) => {
      elementFunc(e, layers);
    });

    canvasRef.current.on("object:scaling", (e) => {
      elementFunc(e, layers);
    });
  }, [layers]);

  useEffect(() => {
    canvasRef.current.on("mouse:down", (e) => {
      if (e.e.button == 2) {
        // console.log(e)
        setActiveObj(e.target);
        setMenuPosition({ top: e.e.clientY, left: e.e.clientX });
        setShowMenu(true);
        return;
      }
      if (e.target == null) {
        setShowMenu(false);
        // console.log(canvasRef.current);
        setCanvaProps((prev) => ({
          ...prev,
          name: canvasRef.current.name,
          height: canvasRef.current.height,
          width: canvasRef.current.width,
          backgroundColor: canvasRef.current.backgroundColor,
        }));
        setWeatherProps({ isSelected: false });
        setTempProps({ isSelected: false });
        setTextTimeProps((prev) => ({ ...prev, isSelected: false }));
        setTextDateProps((prev) => ({ ...prev, isSelected: false }));
        setTextStripProps((prev) => ({ ...prev, isSelected: false }));
        setObjectKey("default");
        setRegisterProps(null);
        setClockWidgetProps({});
        setType("Design");
        return;
      }
      elementFunc(e, layers);
     
      setShowMenu(false);
    });
  }, [layers]);
  useEffect(() => {
    if (isContentAdded) {
      // setFilteredContent([..._content]);
      toast.success("Template stored successfully.");
      onClearResources();
      setType("Design");
      setRegisterProps(null);
      setClockWidgetProps({});
      setObjectKey("default");
      dispatch(contentFlagOff());
    }
    if (isContentUpdated) {
      toast.success("Template updated successfully.");
      // onClearResources();
      setType("Design");
      setObjectKey("default");
      dispatch(contentFlagOff());
    }
  }, [isContentAdded, isContentUpdated]);

  
  const saveHistory = (currentCanvas) => {
    const state = JSON.stringify(currentCanvas.toJSON(CUSTOM_PROPS));
    setHistory((prev) => [...prev, state]);
  };

  // Helper function to restore custom properties after loading JSON
  const restoreCustomProperties = (jsonState) => {
    const objects = canvasRef.current.getObjects();
    const jsonObjects = jsonState.objects || [];
    
    objects.forEach((fabricObject) => {
      // Match by id if available, otherwise match by index
      const originalObject = jsonObjects.find(obj => obj.id === fabricObject.id) || 
                             jsonObjects[objects.indexOf(fabricObject)];
      
      if (originalObject) {
        // Preserve all custom properties
        if (originalObject.id !== undefined && originalObject.id !== '') {
          fabricObject.id = originalObject.id;
        }
        if (originalObject.name !== undefined && originalObject.name !== '') {
          fabricObject.name = originalObject.name;
        }
        if (originalObject.eleType !== undefined && originalObject.eleType !== '') {
          fabricObject.eleType = originalObject.eleType;
        }
        if (originalObject.url !== undefined) {
          fabricObject.url = originalObject.url;
        }
        if (originalObject.linkUrl !== undefined) {
          fabricObject.linkUrl = originalObject.linkUrl;
        }
        if (originalObject._name !== undefined) {
          fabricObject._name = originalObject._name;
        }
        if (originalObject.weatherObj !== undefined) {
          fabricObject.weatherObj = originalObject.weatherObj;
        }
        if (originalObject.tempObj !== undefined) {
          fabricObject.tempObj = originalObject.tempObj;
        }
        if (originalObject.scaletype !== undefined) {
          fabricObject.scaletype = originalObject.scaletype;
        }
        if (originalObject.contents !== undefined) {
          fabricObject.contents = originalObject.contents;
        }
        if (originalObject.playlistId !== undefined) {
          fabricObject.playlistId = originalObject.playlistId;
        }
        if (originalObject.scrollstrip !== undefined) {
          fabricObject.scrollstrip = originalObject.scrollstrip;
        }
        if (originalObject.registerObj !== undefined) {
          fabricObject.registerObj = originalObject.registerObj;
        }
        if (originalObject.clockObj !== undefined) {
          fabricObject.clockObj = originalObject.clockObj;
        }
        if (originalObject.weatherObj !== undefined) {
          fabricObject.weatherObj = originalObject.weatherObj;
        }
      }
      if (fabricObject.eleType === "directory-register") {
        fabricObject.setCoords();
        const rw = (fabricObject.width || 348) * Math.abs(fabricObject.scaleX || 1);
        const rh = (fabricObject.height || 500) * Math.abs(fabricObject.scaleY || 1);
        let ro = fabricObject.registerObj;
        if (ro && (ro._layoutW == null || ro._layoutH == null)) {
          ro = {
            ...ro,
            _layoutW:
              typeof ro._layoutW === "number" && ro._layoutW > 0
                ? ro._layoutW
                : Math.max(1, rw),
            _layoutH:
              typeof ro._layoutH === "number" && ro._layoutH > 0
                ? ro._layoutH
                : Math.max(1, rh),
          };
          fabricObject.registerObj = ro;
        }
        fabricObject._registerLayoutSize = {
          w: Math.max(1, rw),
          h: Math.max(1, rh),
        };
      }
    });
    setLayers(canvasRef.current.getObjects());
    setHasCanvasBackground(!!canvasRef.current.backgroundImage);
  };

  // Undo functionality
  const undo = () => {
    console.log("undo");
    if (history.length > 1) {
      const newHistory = [...history];
      const currentState = newHistory.pop(); // Remove the current state
      setRedoStack((prev) => [...prev, currentState]); // Save the current state to the redo stack
      setHistory(newHistory);

      const previousState = JSON.parse(newHistory[newHistory.length - 1]);
      canvasRef.current.loadFromJSON(previousState, () => {
        restoreCustomProperties(previousState);
        canvasRef.current.requestRenderAll();
      });
    }
  };

  // Redo functionality
  const redo = () => {
    console.log("redo");
    if (redoStack.length > 0) {
      const redoState = redoStack.pop(); // Get the last redo state
      setRedoStack(redoStack); // Update the redo stack
      setHistory((prev) => [...prev, redoState]); // Add redo state to history

      const state = JSON.parse(redoState);
      canvasRef.current.loadFromJSON(state, () => {
        restoreCustomProperties(state);
        canvasRef.current.requestRenderAll();
      });
    }
  };

  const panFunction = () => {
    if (canvasRef.current) {
      canvasRef.current.on("mouse:up", (e) => {
        // console.log(e.e);
        const delta = new fabric.Point(e.e.movementX, e.e.movementY);
        canvasRef.current.relativePan(delta);
        canvasRef.current.renderAll();
      });
    }
  };
  /* DesignCatagory replaced by renderLeftContent + renderRightSidebar */
  const onDrawShapes = (name) => {
    let shape;
    if (canvasRef.current) {
      if (name === "Rect") shape = new EditorOps().createRect();
      if (name === "Circle") shape = new EditorOps().createCircle();
      if (name === "Line") shape = new EditorOps().createLine();
      if (name === "Star") shape = new EditorOps().createPolygone();
      if (name === "Triangle") shape = new EditorOps().createTriangle();
      setShapeProps((prev) => ({
        ...prev,
        fill: "gray",
        stroke: "black",
        strokeWidth: 1,
        isShadow: false,
        shadow: {},
      }));
      setLayers((prev) => [...prev, shape]);
      canvasRef.current.add(shape);
      canvasRef.current.requestRenderAll();
    }
  };
  const onAddEmojiToCanva = (emoji) => {
    const _emoji = new EditorOps().addEmojiToCanvas(emoji);
    setLayers((prev) => [...prev, _emoji]);
    canvasRef.current.add(_emoji);

    canvasRef.current.requestRenderAll();
  };
  const onAddTime = (time) => {
    const _time = new EditorOps().addTextToCanvas(time, "time");
    setTextTimeProps((prev) => ({ ...prev, isSelected: false }));
    setLayers((prev) => [...prev, _time]);
    canvasRef.current.add(_time);
    // setInterval(() => {
    //   const now = new Date();
    //   const formattedTime = now.toLocaleTimeString(); // Format: HH:mm:ss
    //   _time.text = `${formattedTime}`;
    //   // canvas.renderAll();
    //   canvasRef.current.requestRenderAll();
    // }, 1000);
    canvasRef.current.requestRenderAll();
  };
  const onAddTextStrip = () => {
    try {
      if (canvasRef.current) {
        const text = new EditorOps().addTextStripBox(
          scrollStrip.text,
          50,
          scrollStrip
        );
        // console.log(text, scrollStrip);
        text.set({
          scrollstrip: scrollStrip,
        });
        setTextStripProps((prev) => ({ ...prev, isSelected: false }));
        setLayers((prev) => [...prev, text]);
        canvasRef.current.add(text);
        canvasRef.current.requestRenderAll();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onAddAnalogClock = () => {
    if (canvasRef.current) {
      new EditorOps().addAnalogClock(
        publicAssetUrl("analog-clock.jpg"),
        "analog-clock",
        (img) => {
          addCanvasImage(img, "Could not add clock to the layout.");
        }
      );

      // fetch(analogclock)
      //   .then((response) => response.arrayBuffer())
      //   .then((buffer) => {
      //     const blob = new Blob([buffer], { type: "image/png" });
      //     const imgurl = URL.createObjectURL(blob);

      //     new EditorOps().addAnalogClock(imgurl, "analog-clock", (img) => {
      //       setTextTimeProps((prev) => ({ ...prev, isSelected: false }));
      //       setLayers((prev) => [...prev, img]);
      //       canvasRef.current.add(img);

      //       canvasRef.current.requestRenderAll();
      //     });
      //   });
    }
  };

  const onChangeTimeFormate = (formatString) => {
    if (canvasRef.current) {
      const _Time = layers.filter((l) => l.id == id);
      const format = JSON.parse(formatString);
      let time = new Date().toLocaleTimeString(undefined, {
        ...format,
        timeZone: "UTC",
        hourCycle: "h23",
        hour: "2-digit",
        minute: "2-digit",
      });
      _Time[0]?.set("text", time);

      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeTimeZone = (zone) => {
    if (canvasRef.current) {
      const _Time = layers.filter((l) => l.id == id);
      let time = new Date().toLocaleTimeString("en-US", {
        timeZone: zone,
        hourCycle: "h23",
        hour: "2-digit",
        minute: "2-digit",
      });
      _Time[0]?.set("text", time);

      canvasRef.current.requestRenderAll();
    }
  };
  const onAddDate = (date) => {
    const _date = new EditorOps().addTextToCanvas(date, "date");
    // const shadow = new EditorOps().addShadow();
    // _date.set("shadow", shadow);
    setTextDateProps((prev) => ({ ...prev, isSelected: false }));
    setLayers((prev) => [...prev, _date]);
    canvasRef.current.add(_date);
    canvasRef.current.requestRenderAll();
  };

  const onChangeDateFormat = (formatString) => {
    if (canvasRef.current) {
      let currentDate;
      const splited = formatString.split(">");
      const format = JSON.parse(splited[0]);
      // console.log(splited)
      // console.log(format)
      const _date = layers.filter((l) => l.id == id);
      if (splited[1] == "4") {
        currentDate = new Date()
          .toLocaleDateString("en-GB", format)
          .replace("/", "-")
          .replace("/", "-");
      } else {
        currentDate = new Date().toLocaleDateString("en-GB", format);
      }
      _date[0]?.set("text", currentDate);
      canvasRef.current.requestRenderAll();
    }
  };

  // all qr functionality
  function onAddQr(value) {
    const tempCanvas = document.createElement("canvas");
    const qr = new QRious({
      element: tempCanvas,
      value: value,
      size: 150,
    });

    const qrCodeUrl = tempCanvas.toDataURL();
    const formData = new FormData();
    fetch(qrCodeUrl)
      .then((response) => response.arrayBuffer())
      .then((buffor) => {
        const blob = new Blob([buffor], { type: "image/png" });
        formData.append("qrcode", blob, `qr-code.png`);
        api
          .post("/content/uploadqr", formData)
          .then((res) => {
            // const _qrCodeUrl = URL.createObjectURL(blob);
            new EditorOps().addImage(res.data.path, "qr", "qr-code", (img) => {
              if (!img) {
                toast.error("Could not add QR code to the layout.");
                return;
              }
              img.set({ text: value });
              addCanvasImage(img);
            });
          })
          .catch((error) => console.log(error));
      });
    // console.log(blob)
  }
  // all qr functionality end

  function getVideoElement(url) {
    var videoE = document.createElement("video");
    videoE.width = 530;
    videoE.height = 298;
    videoE.loop = true;
    videoE.autoplay = true;
    videoE.muted = true;
    videoE.crossOrigin = "anonymous";
    var source = document.createElement("source");
    source.src = url;
    source.type = "video/mp4";
    videoE.appendChild(source);
    return videoE;
  }
  const onLoadVideoFromDb = (_obj) => {
    // videoPlayerThumbnail
    if (canvasRef.current) {
      new EditorOps().addLocalContent(
        publicAssetUrl("video-icon.png"),
        _obj.url,
        _obj.name,
        "nor-video",
        (img) => {
          addCanvasImage(img, "Could not add video to the layout.");
        }
      );
      // fetch(video_icon)
      // .then(res=>res.arrayBuffer())
      // .then(buffer=>{
      //   const blob = new Blob([buffer], { type: "image/png" });
      //   const imgurl = URL.createObjectURL(blob);
      // new EditorOps().addLocalContent( imgurl,_obj.url, _obj.name,"nor-video",(img)=>{
      //   setLayers((prev) => [...prev, img]);
      //   canvasRef.current.add(img);
      //   canvasRef.current.requestRenderAll();

      // })

      // })
    }
  };
  const onLoadImageFromDb = (url, name) => {
    if (!canvasRef.current) return;
    const active = canvasRef.current.getActiveObject();
    const isNorLayoutImage =
      active &&
      !active._objects &&
      (active.eleType === "nor-image" ||
        (active.type === "nor-image" && !active.contents && !active.weatherObj));

    if (isNorLayoutImage) {
      new EditorOps().replaceNorImage(active, url, name, (newImg) => {
        if (!newImg) {
          new EditorOps().addImage(url, name, "nor-image", (img) => {
            addCanvasImage(img, "Could not load image. The file may be missing locally.");
          });
          return;
        }
        const canvas = canvasRef.current;
        const idx = canvas.getObjects().indexOf(active);
        canvas.remove(active);
        canvas.add(newImg);
        const depth = Math.max(0, canvas.getObjects().length - 1 - idx);
        for (let i = 0; i < depth; i++) {
          if (typeof newImg.sendBackwards === "function") {
            newImg.sendBackwards(false);
          } else if (typeof canvas.sendBackwards === "function") {
            canvas.sendBackwards(newImg);
          } else {
            break;
          }
        }
        canvas.setActiveObject(newImg);
        canvas.requestRenderAll();
        setLayers(canvas.getObjects());
        toast.success("Image replaced (size and position kept)");
      });
      return;
    }

    new EditorOps().addImage(url, name, "nor-image", (img) => {
      addCanvasImage(img, "Could not load image. The file may be missing locally.");
    });
  };

  const getSelectedNorLayoutImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const active = canvas.getActiveObject();
    if (!active || active._objects) return null;
    return EditorOps.isNorLayoutImage(active) ? active : null;
  };

  const onNorImageCoverFullLayout = () => {
    const img = getSelectedNorLayoutImage();
    const canvas = canvasRef.current;
    if (!img || !canvas) {
      toast.error("Select an image on the layout first.");
      return;
    }
    const cw = widthRef.current;
    const ch = heightRef.current;
    EditorOps.fitNorImageCoverLayout(img, cw, ch);
    canvas.sendToBack(img);
    canvas.setActiveObject(img);
    img.setCoords();
    canvas.requestRenderAll();
    setPlaylistProps((prev) => ({
      ...prev,
      left: img.left,
      top: img.top,
      scaleX: img.scaleX,
      scaleY: img.scaleY,
      width: img.width,
      height: img.height,
    }));
    setLayers(canvas.getObjects());
    saveHistory(canvas);
    toast.success("Image covers full layout.");
  };

  const onNorImageSetAsLayoutBackground = () => {
    const img = getSelectedNorLayoutImage();
    const canvas = canvasRef.current;
    if (!img || !canvas) {
      toast.error("Select an image on the layout first.");
      return;
    }
    const cw = widthRef.current;
    const ch = heightRef.current;
    EditorOps.setNorImageAsCanvasBackground(canvas, img, cw, ch, (ok) => {
      if (!ok) {
        toast.error("Could not set layout background.");
        return;
      }
      setLayers(canvas.getObjects());
      setHasCanvasBackground(true);
      setType("Design");
      setId("");
      saveHistory(canvas);
      toast.success("Image set as full-screen layout background.");
    });
  };

  const onClearLayoutBackground = () => {
    if (!canvasRef.current) return;
    canvasRef.current.set("backgroundImage", null);
    canvasRef.current.requestRenderAll();
    setHasCanvasBackground(false);
    saveHistory(canvasRef.current);
    toast.success("Layout background cleared.");
  };

  const onLoadRssFromDb = (url) => {
    if (canvasRef.current) {
      new EditorOps().addLocalContent(
        publicAssetUrl("rss-icon.png"),
        url,
        "",
        "rss",
        (img) => {
          addCanvasImage(img, "Could not add RSS feed to the layout.");
        }
      );
      // fetch(rss_icon)
      //   .then((response) => response.arrayBuffer())
      //   .then((buffer) => {
      //     const blob = new Blob([buffer], { type: "image/png" });

      //     const imgurl = URL.createObjectURL(blob);
      //     new EditorOps().addLocalContent(imgurl, url, "rss", (img) => {
      //       setLayers((prev) => [...prev, img]);
      //       canvasRef.current.add(img);
      //       canvasRef.current.requestRenderAll();
      //     });
      //   });
    }
  };
  const onAddWeather = (obj) => {
    if (canvasRef.current) {
      new EditorOps().addWeather(
        publicAssetUrl("weather_icon.png"),
        obj,
        "weather",
        (img) => {
          addCanvasImage(img, "Could not add weather widget to the layout.");
        }
      );
      // fetch(weather_icon)
      //   .then((response) => response.arrayBuffer())
      //   .then((buffer) => {
      //     const blob = new Blob([buffer], { type: "image/png" });

      //     const imgurl = URL.createObjectURL(blob);
      //     new EditorOps().addWeather(imgurl, obj, "weather", (img) => {
      //       setLayers((prev) => [...prev, img]);
      //       canvasRef.current.add(img);
      //       canvasRef.current.requestRenderAll();
      //     });
      //   });
    }
  };

  const onAddTemprature = (obj) => {
    if (canvasRef.current) {
      new EditorOps().addTemp(
        publicAssetUrl("temp_icon.png"),
        obj,
        "temp",
        (img) => {
          addCanvasImage(img, "Could not add temperature widget to the layout.");
        }
      );
      // fetch(temp_icon)
      //   .then((response) => response.arrayBuffer())
      //   .then((buffer) => {
      //     const blob = new Blob([buffer], { type: "image/png" });

      //     const imgurl = URL.createObjectURL(blob);
      //     new EditorOps().addTemp(imgurl, obj, "temp", (img) => {
      //       setLayers((prev) => [...prev, img]);
      //       canvasRef.current.add(img);
      //       canvasRef.current.requestRenderAll();
      //     });
      //   });
    }
  };

  const onLoadPlaylistFromDb = (_content, name, playlistId) => {
    if (canvasRef.current) {
      new EditorOps().addPlaylist(
        publicAssetUrl("playlist-video.png"),
        name,
        _content,
        (img) => {
          addCanvasImage(img, "Could not add playlist to the layout.");
        },
        playlistId
      );
      // fetch(playlist_icon)
      //   .then((response) => response.arrayBuffer())
      //   .then((buffer) => {
      //     const blob = new Blob([buffer], { type: "image/png" });
      //     const _imgsrc = URL.createObjectURL(blob);
      //     new EditorOps().addPlaylist(_imgsrc,name, _content, (img) => {
      //       setLayers((prev) => [...prev, img]);
      //       canvasRef.current.add(img);
      //       canvasRef.current.requestRenderAll();
      //     });
      //   });
    }
  };
  const getImageFromLocalComputer = (f) => {
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = function () {
      if (canvasRef.current) {
        new fabric.Image.fromURL(reader.result, (img) => {
          img.scale(0.3);
          img.set({
            paintFirst: "fill",
            cornerColor: "black",
            eleType: "nor-image",
            type: "nor-image",
          });

          // img.filters.push(
          //   new fabric.Image.filters.Contrast({ contrast: 0.5 })
          // );
          // img.filters.push(new fabric.Image.filters.Blur(0.5));
          // img.applyFilters();
          setLayers((prev) => [...prev, img]);

          canvasRef.current.add(img);
          // canvasRef.current.requestRenderAll();
        });
      }
    };
  };

  function getVideoElement(url) {
    var videoE = document.createElement("video");
    videoE.width = 530;
    videoE.height = 298;
    videoE.muted = true;
    videoE.crossOrigin = "anonymous";
    var source = document.createElement("source");
    source.src = url;
    source.type = "video/mp4";
    videoE.appendChild(source);
    return videoE;
  }

  const getVideoFromLocalComputer = (v) => {
    const reader = new FileReader();
    reader.readAsDataURL(v);
    reader.onload = function () {
      if (canvasRef.current) {
        const videoE = getVideoElement(reader.result);
        const vid = new fabric.Image(videoE, {
          left: 200,
          top: 300,
          angle: -15,
          originX: "center",
          originY: "center",
          cornerColor: "black",
          objectCaching: false,
        });
        setLayers((prev) => [...prev, vid]);
        canvasRef.current.add(vid);
        // videoEl.load();
        vid.getElement().play();
        canvasRef.current.requestRenderAll();
      }
    };
  };

  const onAlignLeft = (align) => {
    setTextAlign(align);
  };
  const onAlignRight = (align) => {
    setTextAlign(align);
  };
  const onChangeTextAlign = (align, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      if (_element[0]) {
        _element[0].set("textAlign", align);
        cb({ textAlign: align });
        canvasRef.current.requestRenderAll();
      }
    }
  };
  const onChangeFontWeight = (weight, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      if (_element[0]) {
        _element[0].set("fontWeight", weight);
        cb({ fontWeight: weight });
        canvasRef.current.requestRenderAll();
      }
    }
  };
  const onChangeFontStyle = (style, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      if (_element[0]) {
        _element[0].set("fontStyle", style);
        cb({ fontStyle: style });
        canvasRef.current.requestRenderAll();
      }
    }
  };
  const onChangeUnderline = (underline, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      if (_element[0]) {
        _element[0].set("underline", underline);
        cb({ underline: underline });
        canvasRef.current.requestRenderAll();
      }
    }
  };
  const onChangeLinethrough = (linethrough, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      if (_element[0]) {
        _element[0].set("linethrough", linethrough);
        cb({ linethrough: linethrough });
        canvasRef.current.requestRenderAll();
      }
    }
  };
  const onSelectText = (_text, fontsize) => {
    if (canvasRef.current) {
      const text = new EditorOps().addTextbox(_text, fontsize);
      setTextBoxProps((prev) => ({ ...prev, shadow: {}, isShadow: false }));
      // text.on("selected", (e) => console.log(e));
      setLayers((prev) => [...prev, text]);
      canvasRef.current.add(text);
      canvasRef.current.requestRenderAll();
    }
  };
  const onAddLinkElement = (label, eleType, linkUrl) => {
    if (canvasRef.current) {
      const el = new EditorOps().addLinkButton(label, eleType, linkUrl);
      setLayers((prev) => [...prev, el]);
      canvasRef.current.add(el);
      canvasRef.current.requestRenderAll();
    }
  };
  const onAddDirectoryRegister = (data) => {
    if (canvasRef.current) {
      const hasRegister = layers.some((l) => l.eleType === "directory-register");
      if (hasRegister) {
        toast.error("Only one register can be added on canvas.");
        return;
      }
      new EditorOps().addDirectoryRegister(data, (group) => {
        setLayers((prev) => [...prev, group]);
        registerReplaceInProgressRef.current = true;
        try {
          canvasRef.current.add(group);
          group.setCoords();
          group._registerLayoutSize = {
            w: Math.max(
              1,
              (group.width || 0) * Math.abs(group.scaleX || 1)
            ),
            h: Math.max(
              1,
              (group.height || 0) * Math.abs(group.scaleY || 1)
            ),
          };
        } finally {
          registerReplaceInProgressRef.current = false;
        }
        canvasRef.current.requestRenderAll();
      });
    }
  };
  const updateDirectoryRegisterGroup = (groupId, nextRegisterObj, layoutOpts = {}) => {
    const { shrinkToContent = false } = layoutOpts;
    if (!canvasRef.current) return;
    const oldGroup =
      canvasRef.current.getObjects().find((l) => l.id === groupId) ||
      layers.find((l) => l.id === groupId);
    if (!oldGroup || oldGroup.eleType !== "directory-register") return;

    const br = oldGroup.getBoundingRect(true);
    const fabricW = Math.max(
      1,
      (oldGroup.width || 348) * Math.abs(oldGroup.scaleX || 1)
    );
    const fabricH = Math.max(
      1,
      (oldGroup.height || 500) * Math.abs(oldGroup.scaleY || 1)
    );
    const storedW = nextRegisterObj._layoutW;
    const storedH = nextRegisterObj._layoutH;
    const w =
      shrinkToContent &&
      typeof storedW === "number" &&
      !Number.isNaN(storedW) &&
      storedW > 0
        ? storedW
        : fabricW;
    const h =
      shrinkToContent &&
      typeof storedH === "number" &&
      !Number.isNaN(storedH) &&
      storedH > 0
        ? storedH
        : fabricH;
    const newObjects = EditorOps.buildRegisterObjects(nextRegisterObj, w, h, {
      shrinkToContent,
    });
    const newGroup = new fabric.Group(newObjects, {
      left: br.left,
      top: br.top,
      originX: "left",
      originY: "top",
      scaleX: 1,
      scaleY: 1,
      angle: oldGroup.angle,
      flipX: oldGroup.flipX,
      flipY: oldGroup.flipY,
      skewX: oldGroup.skewX,
      skewY: oldGroup.skewY,
      subTargetCheck: false,
      hasBorders: true,
      hasControls: true,
      lockRotation: true,
    });
    const rect0 = newObjects[0];
    const finalW = rect0.width;
    const finalH = rect0.height;
    const mergedRegister = {
      ...nextRegisterObj,
      _layoutW:
        shrinkToContent &&
        typeof storedW === "number" &&
        !Number.isNaN(storedW) &&
        storedW > 0
          ? storedW
          : finalW,
      _layoutH: finalH,
    };

    newGroup.id = oldGroup.id;
    newGroup.eleType = "directory-register";
    newGroup.registerObj = mergedRegister;

    registerReplaceInProgressRef.current = true;
    try {
      canvasRef.current.remove(oldGroup);
      canvasRef.current.add(newGroup);
      newGroup.setCoords();
      newGroup._registerLayoutSize = {
        w: Math.max(
          1,
          (newGroup.width || 0) * Math.abs(newGroup.scaleX || 1)
        ),
        h: Math.max(
          1,
          (newGroup.height || 0) * Math.abs(newGroup.scaleY || 1)
        ),
      };
      canvasRef.current.setActiveObject(newGroup);
    } finally {
      registerReplaceInProgressRef.current = false;
    }

    setLayers((prev) => prev.map((l) => (l.id === groupId ? newGroup : l)));
    const active = canvasRef.current.getActiveObject();
    if (active && active.id === groupId && active.eleType === "directory-register") {
      setRegisterProps({
        ...mergedRegister,
        left: active.left,
        top: active.top,
        width: mergedRegister._layoutW,
        height: mergedRegister._layoutH,
      });
    }
    canvasRef.current.requestRenderAll();
  };
  updateDirectoryRegisterGroupRef.current = updateDirectoryRegisterGroup;
  const genNewElement = (url, index) => {
    let videoE = document.createElement("video");
    videoE.src = url;
    videoE.loop = true;
    videoE.autoplay = true;
    videoE.muted = true;
    videoE.crossOrigin = "anonymous";
    return videoE;
  };

  const onLoadTemplateFromDb = (json, id, name, saveContext = {}) => {
    if (canvasRef.current) {
      try {
        const { layoutOwnerUserId, layoutFolderId } = saveContext;
        layoutContentContextRef.current = {
          ownerUserId: layoutOwnerUserId ?? null,
          folderId:
            layoutFolderId != null && layoutFolderId !== ""
              ? layoutFolderId
              : null,
        };
        const _json = typeof json === "string" ? JSON.parse(json) : json;
        // let videos=_json.objects.filter(j=>j.eleType=='nor-video')
        // let other={..._json,objects:_json.objects.filter(j=>j.eleType!=='nor-video')}
        // console.log(videos)
        // // videos[0].forEach((v,index)=>{
        // let ele=genNewElement(videos[0].url,0)
        // console.log(ele)
        // // canvasRef.current.setDimensions(_json.canvasWidth,_json.canvasHeight)
        // ele.addEventListener("loadeddata",function(){
        //   new fabric.Image.fromURL(videos[0].url,img=>{
        //   img.set({
        //     url:videos[0].url,
        //   //  height:videos[0].height,
        //   //  width:videos[0].width,
        //   //  scaleX:videos[0].scaleX,
        //   //  scaleY:videos[0].scaleY,
        //   //  top:videos[0].top,
        //   //  left:videos[0].left,
        //   //  right:videos[0].right,
        //   //  bottom:videos[0].bottom,
        //    ...videos[0]
        //   })
        //   console.log(img)
        //   setLayers((prev) => [...prev, img]);
        // canvasRef.current.add(img);
        // function render() {
        //   if(canvasRef.current){
        //     img.setElement(ele);
        //     img.getElement().play();
        //     canvasRef.current.requestRenderAll();
        //     requestAnimationFrame(render);
        //   }
        // }
        // render();
        //   },{crossOrigin:'anonymous'})

        // })

        // console.log(other);
        // console.log("Loaded JSON:", JSON.stringify(_json, null, 2));
        // console.log(_json);
        setIsEditMode(true);
        setCanvaId(id);
        setTemplateName(name);

        const rawW = _json.canvasWidth ?? _json.width;
        const rawH = _json.canvasHeight ?? _json.height;
        const w = Math.max(1, Math.round(Number(rawW)) || 1920);
        const h = Math.max(1, Math.round(Number(rawH)) || 1080);

        widthRef.current = w;
        heightRef.current = h;
        isLoadingTemplateRef.current = true;
        setHeight(h);
        setWidth(w);
        const loadedOrientation = w >= h ? "landscape" : "portrait";
        setOrientation(loadedOrientation);
        setCanvasLength({ width: w, height: h });
        aspectRatio.current = w / h;

        _json.canvasWidth = w;
        _json.canvasHeight = h;
        _json.width = w;
        _json.height = h;

        canvasRef.current.setZoom(1);
        canvasRef.current.setDimensions({ width: w, height: h });

        canvasRef.current.loadFromJSON(_json, () => {
          try {
            restoreCustomProperties(_json);
            refreshClockWidgets(canvasRef.current);
            refreshWeatherWidgets(canvasRef.current);
            canvasRef.current.requestRenderAll();
          } finally {
            isLoadingTemplateRef.current = false;
            setTimeout(fitCanvasToScreen, 50);
          }
        });
      } catch (error) {
        console.error("Error parsing JSON:", error);
        isLoadingTemplateRef.current = false;
      }
    }
  };
  const handleWidthChange = (e) => {
    // console.log('width->',e)
    const newWidth = parseInt(e, 10);
    if (!isNaN(newWidth) && newWidth > 0) {
      setWidth(newWidth);
      const newHeight = Math.round(newWidth / aspectRatio.current);
      setHeight(newHeight);
    }
  };
  const handleHeightChange = (e) => {
    // console.log('height->',e)
    const newHeight = parseInt(e, 10);
    if (!isNaN(newHeight) && newHeight > 0) {
      setHeight(newHeight);
      // if (maintainAspectRatio) {
      const newWidth = Math.round(newHeight * aspectRatio.current);
      setWidth(newWidth);
      // }
    }
  };

  const onSetHeightWidthOfCanva = (obj) => {
    if (canvasRef.current) {
      const w = Number(obj.width);
      const h = Number(obj.height);
      setHeight(h);
      setWidth(w);
      setOrientation(w >= h ? "landscape" : "portrait");
      aspectRatio.current = w / h;
    }
  };
  const onGetChangeCanvaBackgroundColor = (color) => {
    if (canvasRef.current) {
      setCanvaColor(color.hex);
      setCanvaProps((prev) => ({
        ...prev,
        backgroundColor: color,
      }));
      canvasRef.current.set("backgroundColor", color);
      canvasRef.current.requestRenderAll();
    }
  };

  // all textbox functions start
  const onChangeShadowState = (_state, cb) => {
    const _shape = layers.filter((l) => l.id == id);
    if (_state) {
      const shadow = new EditorOps().addShadow();
      _shape[0].set({ shadow: shadow });
      cb({
        shadow: { color: "black", blur: 5, offsetY: 10, offsetX: 10 },
        isShadow: true,
      });
      // setTextBoxProps((prev)=>({...prev,shadow:{color:'black',blur:5,offsetY:10,offsetX:10},isShadow:true}));
    } else {
      _shape[0].set({ shadow: {} });
      cb({ shadow: {}, isShadow: false });
      // setTextBoxProps((prev)=>({...prev,shadow:{},isShadow:false}));
    }
    canvasRef.current.requestRenderAll();
  };
  const onChangeColor = (_color, cb) => {
    try {
      if (canvasRef.current) {
        const _element = layers.filter((l) => l.id == id);
        _element[0]?.set(
          "fill",
          `rgba(${_color.r},${_color.g},${_color.b},${_color.a})`
        );
        cb({ fill: `rgba(${_color.r},${_color.g},${_color.b},${_color.a})` });
        canvasRef.current.requestRenderAll();
      }
    } catch (error) {
      toast.error("Select element");
    }
  };

  const onChangeSize = (_size, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      _element[0]?.set("fontSize", _size);
      cb({ fontSize: _size });
      // setTextBoxProps((prev) => ({ ...prev, fontSize: _size }));
      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeShadowBlur = (value, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      _element[0]?.set("shadow", {
        ..._element[0].shadow,
        color: _element[0]?.shadow?.color,
        blur: value,
      });

      cb({ shadow: { ..._element[0].shadow, blur: value } });
      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeShadowColor = (_color, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      _element[0]?.set("shadow", {
        ..._element[0].shadow,
        color: `rgba(${_color.r},${_color.g},${_color.b},${_color.a})`,
        blur: _element[0]?.shadow?.blur,
      });
      cb({
        shadow: {
          ..._element[0].shadow,
          color: `rgba(${_color.r},${_color.g},${_color.b},${_color.a})`,
        },
      });

      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeShadowOffSets = (offsets, cb) => {
    if (canvasRef.current) {
      const _element = layers.filter((l) => l.id == id);
      _element[0]?.set("shadow", {
        ..._element[0].shadow,
        offsetX: offsets.x,
        offsetY: offsets.y,
      });
      cb({
        shadow: {
          // ...prev.shadow,
          ..._element[0].shadow,
          offsetX: offsets.x,
          offsetY: offsets.y,
        },
      });
      canvasRef.current.requestRenderAll();
    }
  };

  const onChangeTextFontTransparency = (_value) => {
    console.log(_value);
  };

  // all shapes functions

  const onChangeBorderColorCompleteOfShape = (_color) => {
    if (canvasRef.current) {
      console.log(_color);
      const _shape = layers.filter((s) => s.id == id);
      _shape[0].set(
        "stroke",
        `rgba(${_color.r},${_color.g},${_color.b},${_color.a})`
      );
      setShapeProps((prev) => ({
        ...prev,
        stroke: `rgba(${_color.r},${_color.g},${_color.b},${_color.a})`,
      }));
      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeBorderThicknessOfShape = (_value) => {
    // console.log(_value)
    if (canvasRef.current) {
      const _shape = layers.filter((s) => s.id == id);
      _shape[0].set({ strokeWidth: _value, strokeUnform: true });
      // _shape[0].set('strokeUniform', true);
      setShapeProps((prev) => ({
        ...prev,
        strokeWidth: _value,
      }));
      canvasRef.current.requestRenderAll();
    }
  };
  const onChangeBorderRoundedOfShape = (_value) => {
    if (canvasRef.current) {
      const _shape = layers.filter((s) => s.id == id);
      _shape[0].set("rx", _value);
      _shape[0].set("ry", _value);
      setShapeProps((prev) => ({
        ...prev,
        radius: _value,
      }));
      canvasRef.current.requestRenderAll();
    }
  };

  // all shapes functions end

  const createVideoElement = (url) => {
    const video = document.createElement("video");
    video.src = url; // Replace with your video URL
    video.crossOrigin = "anonymous"; // Ensure cross-origin compatibility
    video.autoplay = true;
    video.loop = true;
    video.muted = true; // Optional, depends on your use case
    return video;
  };
  const addVideoToCanvas = (videoElement, canvas, setLayers) => {
    videoElement.addEventListener("loadeddata", () => {
      const videoImage = new fabric.Image(videoElement, {
        left: 100,
        top: 100,
        scaleX: 0.5,
        scaleY: 0.5,
        selectable: true,
      });

      // Add the video image to the canvas and update the layers
      canvas.current.add(videoImage);
      canvas.requestRenderAll();
      setLayers((prevLayers) => [...prevLayers, videoImage]);

      console.log("Video successfully added to the canvas.");
    });

    // Handle potential video loading errors
    videoElement.addEventListener("error", (e) => {
      console.error("Failed to load video:", e);
    });
  };
  const onClearResources = () => {
    let allCanvaObjs = canvasRef?.current?.getObjects();
    allCanvaObjs.forEach((ele) => {
      canvasRef?.current?.remove(ele);
    });
    setCanvaProps((prev) => ({
      ...prev,
      backgroundColor: "white",
    }));
    canvasRef.current.set("backgroundColor", "white");
    canvasRef.current.set("backgroundImage", null);
    setHasCanvasBackground(false);
    canvasRef.current.requestRenderAll();
    setLayers([]);
  };
  const onStartNew = () => {
    onClearResources();
    setHeight(600);
    setWidth(900);
    setTemplateName("");
    setIsEditMode(false);
    layoutContentContextRef.current = { ownerUserId: null, folderId: null };
  };
  const onToggleOrientation = (newOrientation) => {
    setOrientation(newOrientation);
    if (newOrientation === "portrait") {
      setWidth(1080);
      setHeight(1920);
      setCanvasLength({ width: 1080, height: 1920 });
      aspectRatio.current = 1080 / 1920;
    } else {
      setWidth(1920);
      setHeight(1080);
      setCanvasLength({ width: 1920, height: 1080 });
      aspectRatio.current = 1920 / 1080;
    }
  };

  const zoomIn = () => {
    if (canvasRef.current) {
      const newZoom = zoom + 0.1;
      setZoom(newZoom);
      canvasRef.current.setZoom(newZoom);
      canvasRef.current.setDimensions({
        width: width * newZoom,
        height: height * newZoom,
      });
    }
  };

  const zoomOut = () => {
    if (canvasRef.current) {
      const newZoom = Math.max(zoom - 0.1, 0.1);
      setZoom(newZoom);
      canvasRef.current.setZoom(newZoom);
      canvasRef.current.setDimensions({
        width: width * newZoom,
        height: height * newZoom,
      });
    }
  };

  const onSaveTemplate = () => {
    if (canvasRef.current) {
      const canvasJSON = canvasRef.current.toJSON(CUSTOM_PROPS);
      // Design size must come from editor state, not getWidth()/getHeight() — those are zoomed
      // for fit-to-screen and corrupt layout on re-edit and on the web player.
      const designW = Math.round(Number(width)) || 1920;
      const designH = Math.round(Number(height)) || 1080;
      canvasJSON.canvasWidth = designW;
      canvasJSON.canvasHeight = designH;
      canvasJSON.width = designW;
      canvasJSON.height = designH;
      canvasJSON.zoom = Math.round(zoom * 100);
      const canvaObj = JSON.stringify(canvasJSON);
      if (templateName === "") {
        toast.error("Template name is required.");
        return;
      }
      dispatch(
        _addFromCanvaTemplate({
          type: "template",
          name: templateName,
          user: contextUserId,
          folderId: _folderId,
          canvaObj,
        })
      );
    }
  };

  const onEditTemplate = () => {
    if (canvasRef.current) {
      const canvasJSON = canvasRef.current.toJSON(CUSTOM_PROPS);
      const designW = Math.round(Number(width)) || 1920;
      const designH = Math.round(Number(height)) || 1080;
      canvasJSON.canvasWidth = designW;
      canvasJSON.canvasHeight = designH;
      canvasJSON.width = designW;
      canvasJSON.height = designH;
      canvasJSON.zoom = Math.round(zoom * 100);
      const canvaObj = JSON.stringify(canvasJSON);
      if (templateName === "") {
        toast.error("Template name is required.");
        return;
      }
      const ctx = layoutContentContextRef.current;
      const saveUser = ctx.ownerUserId ?? contextUserId;
      const saveFolderId = ctx.folderId ?? _folderId;
      dispatch(
        _editToCanvaTemplate(canvaId, {
          type: "template",
          name: templateName,
          user: saveUser,
          folderId: saveFolderId,
          canvaObj,
        })
      );
    }
  };

  const onExportPng = () => {
    if (canvasRef.current) {
      const link = document.createElement("a");
      link.download = "exported_from_notiz.png";
      link.href = canvasRef?.current?.toDataURL();
      link.click();
    }
  };

  const deleteSelectedElement = () => {
    if (canvasRef.current) {
      const active = canvasRef.current.getActiveObject();
      if (active) {
        setLayers(layers.filter((l) => l.id !== active.id));
        canvasRef.current.remove(active);
        canvasRef.current.requestRenderAll();
        setType("Design");
        setRegisterProps(null);
        setClockWidgetProps({});
        setWeatherWidgetProps({});
        setObjectKey("default");
      }
    }
  };

  const onAddClockWidget = (variant) => {
    if (!canvasRef.current) return;
    new EditorOps().addClockWidget({ variant }, (group) => {
      setLayers((prev) => [...prev, group]);
      canvasRef.current.add(group);
      canvasRef.current.setActiveObject(group);
      canvasRef.current.requestRenderAll();
    });
  };

  const onAddWeatherWidget = (variant) => {
    if (!canvasRef.current) return;
    new EditorOps().addWeatherWidget({ variant }, (group) => {
      if (!group) return;
      setLayers((prev) => [...prev, group]);
      canvasRef.current.add(group);
      canvasRef.current.setActiveObject(group);
      canvasRef.current.requestRenderAll();
      refreshWeatherWidgets(canvasRef.current);
    });
  };

  const flushWeatherWidgetRefresh = () => {
    if (!canvasRef.current || !id) return;
    if (weatherRefreshTimerRef.current) {
      clearTimeout(weatherRefreshTimerRef.current);
      weatherRefreshTimerRef.current = null;
    }
    clearWeatherCache();
    refreshWeatherWidgets(canvasRef.current).then(() => {
      const updated = canvasRef.current?.getObjects().find((o) => o.id == id);
      if (updated?.weatherObj) {
        setWeatherWidgetProps((prev) => ({
          ...prev,
          ...updated.weatherObj,
          style: mergeWeatherObjStyle(updated.weatherObj),
        }));
      }
      canvasRef.current?.requestRenderAll();
    });
  };

  const patchWeatherWidget = (partial) => {
    if (!canvasRef.current || !id) return;
    const el = canvasRef.current.getObjects().find((o) => o.id == id);
    if (!el || el.eleType !== "weather-widget") return;

    const locationPatch = { ...partial };
    if ("city" in locationPatch) {
      locationPatch.lat = "";
      locationPatch.lng = "";
    }

    const nextWeatherObj = {
      ...(el.weatherObj || {}),
      ...locationPatch,
      variant: locationPatch.variant || el.weatherObj?.variant || "glass-card",
      style: locationPatch.style
        ? { ...(el.weatherObj?.style || {}), ...locationPatch.style }
        : el.weatherObj?.style || {},
    };

    el.weatherObj = nextWeatherObj;
    applyWeatherWidgetStyle(el, nextWeatherObj);
    canvasRef.current.requestRenderAll();
    saveHistory(canvasRef.current);
    setWeatherWidgetProps((prev) => ({
      ...prev,
      ...nextWeatherObj,
      style: mergeWeatherObjStyle(nextWeatherObj),
    }));

    const needsFetch =
      "city" in partial ||
      "lat" in partial ||
      "lng" in partial ||
      "unit" in partial;

    if (needsFetch) {
      clearWeatherCache();
      if (weatherRefreshTimerRef.current) {
        clearTimeout(weatherRefreshTimerRef.current);
      }
      const delay = "city" in partial ? 500 : 0;
      weatherRefreshTimerRef.current = setTimeout(() => {
        flushWeatherWidgetRefresh();
      }, delay);
    }
  };

  const onRsRegisterChange = (key, value) => {
    const next = { ...registerProps, [key]: value };
    setRegisterProps(next);
    if (id) updateDirectoryRegisterGroup(id, next, { shrinkToContent: true });
  };

  const patchClockWidgetStyle = (partial) => {
    if (!canvasRef.current || !id) return;
    const el = layers.find((l) => l.id === id);
    if (!el || el.eleType !== "clock-widget") return;
    const nextStyle = { ...mergeClockObjStyle(el.clockObj), ...partial };
    const nextClockObj = {
      ...(el.clockObj || {}),
      variant: el.clockObj?.variant || "gradient-night",
      style: nextStyle,
    };
    el.clockObj = nextClockObj;
    applyClockWidgetStyle(el, nextClockObj);
    canvasRef.current.requestRenderAll();
    saveHistory(canvasRef.current);
    setClockWidgetProps((prev) => ({
      ...prev,
      variant: nextClockObj.variant,
      style: nextStyle,
    }));
  };

  const bringElementForward = () => {
    if (canvasRef.current) {
      const act = canvasRef.current.getActiveObject();
      if (act) {
        canvasRef.current.bringToFront(act);
        canvasRef.current.renderAll();
      }
    }
  };

  const sendElementBackward = () => {
    if (canvasRef.current) {
      const act = canvasRef.current.getActiveObject();
      if (act) {
        canvasRef.current.sendToBack(act);
        canvasRef.current.requestRenderAll();
      }
    }
  };

  const onRsChangeParams = (e) => {
    const { name, value } = e.target;
    const numVal = parseFloat(value);
    if (isNaN(numVal)) return;
    if (canvasRef.current) {
      const el = layers.find((l) => l.id === id);
      if (!el) return;
      el.set({ [name]: numVal });
      el.setCoords();
      canvasRef.current.requestRenderAll();
      if (type === "ToolPlaylistOp") {
        setPlaylistProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "Text") {
        setTextBoxProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "Time") {
        setTextTimeProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "Date") {
        setTextDateProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "QRCode") {
        setQrProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "Weather") {
        setWeatherProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "Temprature") {
        setTempProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "TextStrip") {
        setTextStripProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "DirectoryRegister") {
        if (["width", "height", "scaleX", "scaleY"].includes(name)) {
          const reg = el.registerObj;
          if (reg) {
            updateDirectoryRegisterGroup(el.id, reg);
          }
          return;
        }
        setRegisterProps((prev) => ({
          ...prev,
          [name]: numVal,
        }));
      } else if (type === "ClockWidget") {
        setClockWidgetProps((prev) => ({ ...prev, [name]: numVal }));
      } else if (type === "WeatherWidget") {
        setWeatherWidgetProps((prev) => ({ ...prev, [name]: numVal }));
      }
    }
  };

  const onRsScaleTypeChange = (fitXY) => {
    if (canvasRef.current) {
      const el = layers.find((l) => l.id === id);
      if (!el) return;
      const obj = fitXY ? { fitXY: true, none: false } : { fitXY: false, none: true };
      el.set({ scaletype: obj });
      setPlaylistProps((prev) => ({ ...prev, ...obj }));
      canvasRef.current.requestRenderAll();
    }
  };

  const getActiveProps = () => {
    if (type === "ToolPlaylistOp") return playlistProps;
    if (type === "Text" || type === "TextStrip") return textStripProps?.isSelected ? textStripProps : textBoxProps;
    if (type === "Time") return textTimeProps;
    if (type === "Date") return textDateProps;
    if (type === "QRCode") return qrProps;
    if (type === "Weather") return weatherProps;
    if (type === "Temprature") return tempProps;
    if (type === "Login" || type === "Register") {
      const el = layers.find((l) => l.id === id);
      return el ? { left: el.left, top: el.top, width: el.width, height: el.height } : {};
    }
    if (type === "DirectoryRegister") return registerProps || {};
    if (type === "ClockWidget") return clockWidgetProps || {};
    if (type === "WeatherWidget") return weatherWidgetProps || {};
    return {};
  };

  const getElementTypeName = () => {
    if (type === "ToolPlaylistOp") {
      if (playlistProps.type === "nor-image") return "Image";
      if (playlistProps.type === "nor-video") return "Video";
      return "Playlist";
    }
    if (type === "Text") return "Text";
    if (type === "TextStrip") return "Text Strip";
    if (type === "Time") return "Time";
    if (type === "Date") return "Date";
    if (type === "QRCode") return "QR Code";
    if (type === "Weather") return "Weather";
    if (type === "Temprature") return "Temperature";
    if (type === "Login") return "Login";
    if (type === "Register") return "Register";
    if (type === "DirectoryRegister") return "Register";
    if (type === "ClockWidget") return "Clock widget";
    if (type === "WeatherWidget") return "Weather widget";
    return "Element";
  };

  const hasElementSelected = type !== "Design";

  useEffect(() => {
    if (!isMobile) return;
    if (hasElementSelected) {
      setMobileLeftOpen(false);
      setMobileRightOpen(true);
    } else {
      setMobileRightOpen(false);
    }
  }, [type, isMobile, hasElementSelected]);

  const closeMobilePanels = () => {
    setMobileLeftOpen(false);
    setMobileRightOpen(false);
  };

  const toggleMobileLeft = () => {
    setMobileLeftOpen((open) => !open);
    setMobileRightOpen(false);
  };

  const toggleMobileRight = () => {
    setMobileRightOpen((open) => !open);
    setMobileLeftOpen(false);
  };

  const renderTextPanel = () => (
    <Text
      onSelectText={onSelectText}
      onAlignLeft={onAlignLeft}
      onAlignRight={onAlignRight}
      textProps={{
        onChangeParams: (obj) => {
          if (canvasRef.current) {
            let _elementEl = layers.find((l) => l.id === id);
            if (!_elementEl) return;
            _elementEl.set({ left: obj.left, top: obj.top, width: obj.width, height: obj.height });
            setTextBoxProps((prev) => ({ ...prev, ...obj }));
            _elementEl.setCoords();
            canvasRef.current.requestRenderAll();
          }
        },
        onChangeTextFontColor: onChangeColor,
        onChangeTextFontSize: onChangeSize,
        onChangeTextAlign: onChangeTextAlign,
        onChangeFontWeight: onChangeFontWeight,
        onChangeFontStyle: onChangeFontStyle,
        onChangeUnderline: onChangeUnderline,
        onChangeLinethrough: onChangeLinethrough,
        onChangeTextFontTransparency: onChangeTextFontTransparency,
        onChangeTextBoxShadowBlur: onChangeShadowBlur,
        onChangeTextBoxShadowColor: onChangeShadowColor,
        onGetOffsetVal: onChangeShadowOffSets,
        onChangeTextBoxShadowState: onChangeShadowState,
        onSetAllProperties: (p) => setTextBoxProps((prev) => ({ ...prev, ...p })),
        textBoxProps: textBoxProps,
      }}
    />
  );

  const renderLeftContent = () => {
    if (leftTab === "media") {
      return (
        <>
          <p className="de-media-replace-hint" style={{ fontSize: 12, color: "var(--de-text-dim)", margin: "0 0 10px", lineHeight: 1.4 }}>
            Select an image on the layout, then click a thumbnail here to replace it without changing size or position.
          </p>
          <div className="de-media-grid">
            {filteredImages.map((con, i) => (
              <div key={con._id || i} className="de-media-item" onClick={() => onLoadImageFromDb(con?.url, con?.name)}>
                <img src={resolveMediaUrl(con.url)} alt={con.name} />
                <span className="de-media-label">{con?.name?.substring(0, 20)}</span>
              </div>
            ))}
            {filteredVideos.map((con, i) => (
              <div key={con._id || i} className="de-media-item" onClick={() => onLoadVideoFromDb({ url: con?.url, name: con?.name })}>
                <video src={resolveMediaUrl(con?.url)} muted />
                <span className="de-media-label">{con?.name?.substring(0, 20)}</span>
              </div>
            ))}
          </div>
          {filteredImages.length === 0 && filteredVideos.length === 0 && (
            <p style={{ textAlign: "center", color: "var(--de-text-dim)", padding: "20px 0", fontSize: 12 }}>No media found</p>
          )}
        </>
      );
    }
    if (leftTab === "playlists") {
      return (
        <>
          <div className="de-media-grid">
            {filteredPlaylists.map((con, i) => (
              <div key={con._id || i} className="de-media-item" onClick={() => onLoadPlaylistFromDb(con?.content, con?.name, con?._id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <MonitorPlay size={32} style={{ color: "var(--de-text-dim)" }} />
                <span className="de-media-label">{con?.name?.substring(0, 20)}</span>
              </div>
            ))}
          </div>
          {filteredPlaylists.length === 0 && (
            <p style={{ textAlign: "center", color: "var(--de-text-dim)", padding: "20px 0", fontSize: 12 }}>No playlists found</p>
          )}
        </>
      );
    }
    if (leftTab === "register") {
      const hasRegisterOnCanvas = layers.some(
        (l) => l.eleType === "directory-register"
      );
      const editingRegisterOnCanvas =
        hasRegisterOnCanvas && type === "DirectoryRegister";
      return (
        <DirectoryRegister
          registerData={
            editingRegisterOnCanvas ? registerProps : null
          }
          onRegisterDataChange={(next) => {
            if (editingRegisterOnCanvas && id) {
              setRegisterProps(next);
              updateDirectoryRegisterGroup(id, next, { shrinkToContent: true });
            }
          }}
          onAddToCanvas={
            hasRegisterOnCanvas ? undefined : onAddDirectoryRegister
          }
          isEditing={editingRegisterOnCanvas}
          editingRegisterObjectId={
            editingRegisterOnCanvas ? id : null
          }
        />
      );
    }
    if (leftTab === "apps") {
      return (
        <>
          <section className="de-text-section">
            <h3 className="de-text-section-title">Clock</h3>
            <ClockWidgetPicker onAddClockWidget={onAddClockWidget} />
          </section>
          <section className="de-text-section" style={{ marginTop: 16 }}>
            <h3 className="de-text-section-title">Weather</h3>
            <WeatherWidgetPicker onAddWeatherWidget={onAddWeatherWidget} />
          </section>
        </>
      );
    }
    if (leftTab === "text") {
      return (
        <>
          {renderTextPanel()}
          <section className="de-text-section" style={{ marginTop: 16 }}>
            <h3 className="de-text-section-title">Login & Register</h3>
            <div className="de-text-presets" style={{ flexDirection: "column", gap: 8 }}>
              <button
                className="de-text-preset"
                onClick={() => onAddLinkElement("Login", "login", "/login")}
              >
                <LogIn size={18} />
                <span className="de-text-preset-label">Login</span>
              </button>
              <button
                className="de-text-preset"
                onClick={() => onAddLinkElement("Register", "register", "/register")}
              >
                <UserPlus size={18} />
                <span className="de-text-preset-label">Register</span>
              </button>
            </div>
          </section>
        </>
      );
    }
    return null;
  };

  const renderRightSidebar = () => {
    if (!hasElementSelected) {
      return (
        <>
          <div className="de-rs-header">
            <h3>Canvas</h3>
          </div>
          <div className="de-rs-body">
            <div className="de-rs-section">
              <span className="de-rs-section-label">Background</span>
              <div className="de-rs-row">
                <button
                  className="de-rs-color-swatch"
                  style={{ background: canvaColor }}
                  onClick={(e) => setColorAnchorEl(e.currentTarget)}
                />
                <Popover
                  open={Boolean(colorAnchorEl)}
                  anchorEl={colorAnchorEl}
                  onClose={() => setColorAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <SketchPicker
                    color={canvaColor}
                    onChangeComplete={(c) => onGetChangeCanvaBackgroundColor(c.hex)}
                  />
                </Popover>
              </div>
            </div>
            <div className="de-rs-section">
              <span className="de-rs-section-label">Orientation</span>
              <div className="de-orientation-toggle">
                <button
                  className={`de-orientation-btn ${orientation === "landscape" ? "active" : ""}`}
                  onClick={() => onToggleOrientation("landscape")}
                >
                  <span className="de-orient-icon de-orient-landscape" />
                  Landscape
                </button>
                <button
                  className={`de-orientation-btn ${orientation === "portrait" ? "active" : ""}`}
                  onClick={() => onToggleOrientation("portrait")}
                >
                  <span className="de-orient-icon de-orient-portrait" />
                  Portrait
                </button>
              </div>
            </div>
            <div className="de-rs-section">
              <span className="de-rs-section-label">Dimensions</span>
              <div className="de-rs-row">
                <div className="de-rs-field">
                  <label>Width</label>
                  <input type="number" value={canvasLength.width} name="width" onChange={(e) => setCanvasLength((p) => ({ ...p, width: e.target.value }))} />
                </div>
                <div className="de-rs-field">
                  <label>Height</label>
                  <input type="number" value={canvasLength.height} name="height" onChange={(e) => setCanvasLength((p) => ({ ...p, height: e.target.value }))} />
                </div>
              </div>
              <button className="de-btn" style={{ marginTop: 8, width: "100%" }} onClick={() => onSetHeightWidthOfCanva(canvasLength)}>
                Apply Size
              </button>
            </div>
            <div className="de-rs-section">
              <span className="de-rs-section-label">Zoom</span>
              <div className="de-rs-zoom-controls">
                <button className="de-rs-zoom-btn" onClick={zoomOut}><Minus size={14} /></button>
                <span className="de-rs-zoom-value">{Math.round(zoom * 100)}%</span>
                <button className="de-rs-zoom-btn" onClick={zoomIn}><Plus size={14} /></button>
              </div>
            </div>
            <div className="de-rs-section">
              <span className="de-rs-section-label">Actions</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button className="de-btn" style={{ width: "100%" }} onClick={onExportPng}>Export PNG</button>
                {hasCanvasBackground && (
                  <button
                    type="button"
                    className="de-btn"
                    style={{ width: "100%" }}
                    onClick={onClearLayoutBackground}
                  >
                    <ImageOff size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                    Clear layout background
                  </button>
                )}
                <button className="de-btn" style={{ width: "100%", color: "var(--de-danger)" }} onClick={onClearResources}>
                  <Trash2 size={14} /> Clear Canvas
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }

    const props = getActiveProps();
    const elemName = getElementTypeName();
    const isScalable = ["ToolPlaylistOp", "QRCode", "Weather", "Temprature", "Time", "Date", "TextStrip", "ClockWidget", "WeatherWidget"].includes(type);

    return (
      <>
        <div className="de-rs-header">
          <h3>{elemName}</h3>
          <button className="de-rs-delete-btn" onClick={deleteSelectedElement}>
            <Trash2 size={15} />
          </button>
        </div>
        <div className="de-rs-body">
          {/* Position */}
          <div className="de-rs-section">
            <div className="de-rs-row">
              <div className="de-rs-field">
                <label>X</label>
                <input type="number" value={Math.round(props?.left || 0)} name="left" onChange={onRsChangeParams} />
              </div>
              <div className="de-rs-field">
                <label>Y</label>
                <input type="number" value={Math.round(props?.top || 0)} name="top" onChange={onRsChangeParams} />
              </div>
            </div>
            {!isScalable && (
              <div className="de-rs-row">
                <div className="de-rs-field">
                  <label>Width</label>
                  <input type="number" value={Math.round(props?.width || 0)} name="width" onChange={onRsChangeParams} />
                </div>
                <div className="de-rs-field">
                  <label>Height</label>
                  <input type="number" value={Math.round(props?.height || 0)} name="height" onChange={onRsChangeParams} />
                </div>
              </div>
            )}
            {isScalable && (
              <div className="de-rs-row">
                <div className="de-rs-field">
                  <label>Width</label>
                  <input type="number" value={Math.round(props?.width || 0)} readOnly style={{ background: "#f8fafc" }} />
                </div>
                <div className="de-rs-field">
                  <label>Height</label>
                  <input type="number" value={Math.round(props?.height || 0)} readOnly style={{ background: "#f8fafc" }} />
                </div>
              </div>
            )}
          </div>

          {/* Layer */}
          <div className="de-rs-section">
            <span className="de-rs-section-label">Layer</span>
            <div className="de-rs-layer-btns">
              <button className="de-rs-layer-btn" onClick={bringElementForward}><ChevronUp size={14} /> Up</button>
              <button className="de-rs-layer-btn" onClick={sendElementBackward}><ChevronDown size={14} /> Down</button>
            </div>
          </div>

          {/* Item Info */}
          <div className="de-rs-section">
            <div className="de-rs-item-info">
              <div className="de-rs-item-icon">
                {elemName === "Image" && <ImageIcon size={16} />}
                {elemName === "Video" && <VideoIcon size={16} />}
                {elemName === "Playlist" && <MonitorPlay size={16} />}
                {elemName === "Text" && <Type size={16} />}
                {elemName === "Login" && <LogIn size={16} />}
                {type === "ClockWidget" && <Clock size={16} />}
                {type === "WeatherWidget" && <CloudSun size={16} />}
                {type === "DirectoryRegister" && <List size={16} />}
                {elemName === "Register" && type !== "DirectoryRegister" && <UserPlus size={16} />}
                {!["Image", "Video", "Playlist", "Text", "Login", "Register"].includes(elemName) && type !== "DirectoryRegister" && type !== "ClockWidget" && type !== "WeatherWidget" && <Layers size={16} />}
              </div>
              <div className="de-rs-item-meta">
                <span className="de-rs-item-id">{id?.substring(0, 20) || "element"}</span>
                <span className="de-rs-item-type-badge">{elemName}</span>
              </div>
            </div>
          </div>

          {type === "ClockWidget" && props?.variant && (
            <ClockWidgetStylePanel
              variant={props.variant}
              style={mergeClockObjStyle({
                variant: props.variant,
                style: props.style,
              })}
              onPatch={patchClockWidgetStyle}
            />
          )}

          {type === "WeatherWidget" && props?.variant && (
            <WeatherWidgetStylePanel
              variant={props.variant}
              style={mergeWeatherObjStyle({
                variant: props.variant,
                style: props.style,
              })}
              city={props.city}
              lat={props.lat}
              lng={props.lng}
              unit={props.unit}
              onPatch={patchWeatherWidget}
              onLocationCommit={flushWeatherWidgetRefresh}
            />
          )}

          {/* Scale Mode - for images/videos/playlists */}
          {type === "ToolPlaylistOp" && (
            <div className="de-rs-section">
              <span className="de-rs-section-label">Scale Mode</span>
              <div className="de-rs-scale-mode">
                <button
                  className={`de-rs-scale-btn ${props?.fitXY ? "active" : ""}`}
                  onClick={() => onRsScaleTypeChange(true)}
                >
                  {props?.fitXY && <Check size={12} />} Fit XY
                </button>
                <button
                  className={`de-rs-scale-btn ${props?.none ? "active" : ""}`}
                  onClick={() => onRsScaleTypeChange(false)}
                >
                  None
                </button>
              </div>
            </div>
          )}

          {type === "ToolPlaylistOp" && playlistProps.type === "nor-image" && (
            <div className="de-rs-section">
              <span className="de-rs-section-label">Full layout</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  type="button"
                  className="de-rs-layer-btn"
                  style={{ width: "100%", justifyContent: "center", gap: 6 }}
                  onClick={onNorImageCoverFullLayout}
                >
                  <Maximize2 size={14} /> Cover full layout
                </button>
                <button
                  type="button"
                  className="de-rs-layer-btn"
                  style={{ width: "100%", justifyContent: "center", gap: 6 }}
                  onClick={onNorImageSetAsLayoutBackground}
                >
                  <Wallpaper size={14} /> Set as layout background
                </button>
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--de-text-dim)",
                  margin: "8px 0 0",
                  lineHeight: 1.35,
                }}
              >
                Both fill the layout. Background is behind everything and removes this image from the layer list.
              </p>
            </div>
          )}

          {/* Scale for scalable elements */}
          {isScalable && (
            <>
              <div className="de-rs-section">
                <span className="de-rs-section-label">Scale</span>
                <div className="de-rs-row">
                  <div className="de-rs-field">
                    <label>Scale X</label>
                    <input type="number" step="0.1" min="0.1" value={+(props?.scaleX || 1).toFixed(1)} name="scaleX" onChange={onRsChangeParams} />
                  </div>
                  <div className="de-rs-field">
                    <label>Scale Y</label>
                    <input type="number" step="0.1" min="0.1" value={+(props?.scaleY || 1).toFixed(1)} name="scaleY" onChange={onRsChangeParams} />
                  </div>
                </div>
              </div>
              <div className="de-rs-section">
                <span className="de-rs-section-label">Computed Size</span>
                <div className="de-rs-row">
                  <div className="de-rs-computed">
                    <span className="de-rs-computed-value">{Math.round((props?.width || 0) * (props?.scaleX || 1))}</span>
                    <span className="de-rs-computed-label">PX Width</span>
                  </div>
                  <div className="de-rs-computed">
                    <span className="de-rs-computed-value">{Math.round((props?.height || 0) * (props?.scaleY || 1))}</span>
                    <span className="de-rs-computed-label">PX Height</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Register (directory) styling */}
          {type === "DirectoryRegister" && registerProps && (
            <div className="de-rs-section">
              <span className="de-rs-section-label">Register</span>
              <div className="de-rs-row">
                <label className="de-rs-inline-label">Background</label>
                <button
                  type="button"
                  className="de-rs-color-swatch"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background:
                      registerProps.backgroundColor === "transparent"
                        ? "repeating-conic-gradient(#888 0% 25%, transparent 0% 50%) 50% / 10px 10px"
                        : (typeof registerProps.backgroundColor === "string"
                          ? registerProps.backgroundColor
                          : `rgb(${registerProps.backgroundColor?.r ?? 0},${registerProps.backgroundColor?.g ?? 0},${registerProps.backgroundColor?.b ?? 0})`),
                  }}
                  onClick={(e) => setRegisterBgAnchorEl(e.currentTarget)}
                />
                <Popover
                  open={Boolean(registerBgAnchorEl)}
                  anchorEl={registerBgAnchorEl}
                  onClose={() => setRegisterBgAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <SketchPicker
                    color={registerProps.backgroundColor === "transparent" ? "#1e293b" : (typeof registerProps.backgroundColor === "string" ? registerProps.backgroundColor : "#1e293b")}
                    onChangeComplete={(c) => {
                      onRsRegisterChange("backgroundColor", c.hex);
                      setRegisterBgAnchorEl(null);
                    }}
                  />
                </Popover>
                <label className="de-rs-inline-label" style={{ marginLeft: 4 }}>
                  <input
                    type="checkbox"
                    checked={registerProps.backgroundColor === "transparent"}
                    onChange={(e) =>
                      onRsRegisterChange(
                        "backgroundColor",
                        e.target.checked ? "transparent" : "#1e293b"
                      )
                    }
                  />{" "}
                  None
                </label>
              </div>
              <div className="de-rs-control">
                <div className="de-rs-slider-row">
                  <label className="de-rs-control-label" htmlFor="de-reg-corner-radius">Corner radius</label>
                  <span className="de-rs-range-value">{registerProps.cornerRadius ?? 0}</span>
                </div>
                <input
                  id="de-reg-corner-radius"
                  type="range"
                  className="de-rs-range"
                  min={0}
                  max={24}
                  value={registerProps.cornerRadius ?? 0}
                  onChange={(e) =>
                    onRsRegisterChange("cornerRadius", Number(e.target.value))
                  }
                />
              </div>
              <div className="de-rs-control">
                <div className="de-rs-slider-row">
                  <label className="de-rs-control-label" htmlFor="de-reg-padding">Padding</label>
                  <span className="de-rs-range-value">{registerProps.padding ?? 8}</span>
                </div>
                <input
                  id="de-reg-padding"
                  type="range"
                  className="de-rs-range"
                  min={0}
                  max={24}
                  value={registerProps.padding ?? 8}
                  onChange={(e) =>
                    onRsRegisterChange("padding", Number(e.target.value))
                  }
                />
              </div>
              <div className="de-rs-control">
                <div className="de-rs-slider-row">
                  <label className="de-rs-control-label" htmlFor="de-reg-title-size">Title size</label>
                  <span className="de-rs-range-value">{registerProps.titleSize ?? 22}</span>
                </div>
                <input
                  id="de-reg-title-size"
                  type="range"
                  className="de-rs-range"
                  min={12}
                  max={36}
                  value={registerProps.titleSize ?? 22}
                  onChange={(e) =>
                    onRsRegisterChange("titleSize", Number(e.target.value))
                  }
                />
              </div>
              <div className="de-rs-control">
                <div className="de-rs-slider-row">
                  <label className="de-rs-control-label" htmlFor="de-reg-floor-size">Floor size</label>
                  <span className="de-rs-range-value">{registerProps.floorSize ?? 16}</span>
                </div>
                <input
                  id="de-reg-floor-size"
                  type="range"
                  className="de-rs-range"
                  min={10}
                  max={28}
                  value={registerProps.floorSize ?? 16}
                  onChange={(e) =>
                    onRsRegisterChange("floorSize", Number(e.target.value))
                  }
                />
              </div>
              <div className="de-rs-control">
                <div className="de-rs-slider-row">
                  <label className="de-rs-control-label" htmlFor="de-reg-row-size">Row size</label>
                  <span className="de-rs-range-value">{registerProps.rowSize ?? 13}</span>
                </div>
                <input
                  id="de-reg-row-size"
                  type="range"
                  className="de-rs-range"
                  min={8}
                  max={22}
                  value={registerProps.rowSize ?? 13}
                  onChange={(e) =>
                    onRsRegisterChange("rowSize", Number(e.target.value))
                  }
                />
              </div>
              <div className="de-rs-row" style={{ marginTop: 6 }}>
                <span className="de-rs-section-label">Text color</span>
                <button
                  type="button"
                  className="de-rs-color-swatch"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background:
                      typeof registerProps.textColor === "string"
                        ? registerProps.textColor
                        : `rgb(${registerProps.textColor?.r ?? 255},${registerProps.textColor?.g ?? 255},${registerProps.textColor?.b ?? 255})`,
                  }}
                  onClick={(e) => setRegisterTextColorAnchorEl(e.currentTarget)}
                />
                <Popover
                  open={Boolean(registerTextColorAnchorEl)}
                  anchorEl={registerTextColorAnchorEl}
                  onClose={() => setRegisterTextColorAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <SketchPicker
                    color={
                      typeof registerProps.textColor === "string"
                        ? registerProps.textColor
                        : registerProps.textColor
                    }
                    onChangeComplete={(c) => {
                      onRsRegisterChange("textColor", c.hex);
                      setRegisterTextColorAnchorEl(null);
                    }}
                  />
                </Popover>
              </div>
            </div>
          )}

          {/* Text-specific controls */}
          {type === "Text" && (
            <>
              <div className="de-rs-section">
                <span className="de-rs-section-label">Font</span>
                <div className="de-rs-row">
                  <div className="de-rs-field">
                    <label>Color</label>
                    <button
                      className="de-rs-color-swatch"
                      style={{ background: textBoxProps?.fill || "#000", width: "100%", height: 32, borderRadius: 6 }}
                      onClick={(e) => setRsColorAnchorEl(e.currentTarget)}
                    />
                    <Popover
                      open={Boolean(rsColorAnchorEl)}
                      anchorEl={rsColorAnchorEl}
                      onClose={() => setRsColorAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                      <SketchPicker
                        color={textBoxProps?.fill || "#000"}
                        onChangeComplete={(c) => {
                          onChangeColor(c.rgb, (p) => setTextBoxProps((prev) => ({ ...prev, ...p })));
                        }}
                      />
                    </Popover>
                  </div>
                  <div className="de-rs-field">
                    <label>Size</label>
                    <input
                      type="number"
                      value={textBoxProps?.fontSize || 16}
                      onChange={(e) => onChangeSize(+e.target.value, (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                    />
                  </div>
                </div>
              </div>
              <div className="de-rs-section">
                <span className="de-rs-section-label">Alignment</span>
                <div className="de-rs-text-align">
                  {["left", "center", "right"].map((a) => (
                    <button
                      key={a}
                      className={`de-rs-text-align-btn ${textBoxProps?.textAlign === a ? "active" : ""}`}
                      onClick={() => onChangeTextAlign(a, (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                      style={{ textTransform: "capitalize", fontSize: 11 }}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div className="de-rs-section">
                <span className="de-rs-section-label">Style</span>
                <div className="de-rs-text-style">
                  <button
                    className={`de-rs-text-style-btn ${textBoxProps?.fontWeight === "bold" ? "active" : ""}`}
                    onClick={() => onChangeFontWeight(textBoxProps?.fontWeight === "bold" ? "normal" : "bold", (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                  >B</button>
                  <button
                    className={`de-rs-text-style-btn ${textBoxProps?.fontStyle === "italic" ? "active" : ""}`}
                    onClick={() => onChangeFontStyle(textBoxProps?.fontStyle === "italic" ? "normal" : "italic", (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                    style={{ fontStyle: "italic" }}
                  >I</button>
                  <button
                    className={`de-rs-text-style-btn ${textBoxProps?.underline ? "active" : ""}`}
                    onClick={() => onChangeUnderline(!textBoxProps?.underline, (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                    style={{ textDecoration: "underline" }}
                  >U</button>
                  <button
                    className={`de-rs-text-style-btn ${textBoxProps?.linethrough ? "active" : ""}`}
                    onClick={() => onChangeLinethrough(!textBoxProps?.linethrough, (p) => setTextBoxProps((prev) => ({ ...prev, ...p })))}
                    style={{ textDecoration: "line-through" }}
                  >S</button>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  return (
      <div className={`de-container${isMobile ? " de-mobile" : ""}`}>
        {isMobile && (mobileLeftOpen || mobileRightOpen) && (
          <button
            type="button"
            className="de-mobile-backdrop"
            aria-label="Close panels"
            onClick={closeMobilePanels}
          />
        )}
        {/* ── Top Bar ── */}
        <header className="de-topbar">
          <div className="de-topbar-left">
            {isMobile && (
              <button
                type="button"
                className={`de-btn de-btn-icon de-mobile-panel-btn${mobileLeftOpen ? " active" : ""}`}
                onClick={toggleMobileLeft}
                title="Elements panel"
                aria-label="Toggle elements panel"
              >
                <PanelLeft size={16} />
              </button>
            )}
            <button className="de-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={18} />
            </button>
            <input
              type="text"
              className="de-template-name-input"
              placeholder="Untitled layout"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>

          <div className="de-topbar-center">
            <button className="de-canvas-dims" onClick={(e) => setSizeAnchorEl(e.currentTarget)}>
              {width}×{height}
            </button>
            <Popover
              open={Boolean(sizeAnchorEl)}
              anchorEl={sizeAnchorEl}
              onClose={() => setSizeAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <div className="de-popover-content">
                <div className="de-popover-row">
                  <label>Width</label>
                  <input type="number" value={canvasLength.width} onChange={(e) => setCanvasLength((p) => ({ ...p, width: e.target.value }))} />
                </div>
                <div className="de-popover-row">
                  <label>Height</label>
                  <input type="number" value={canvasLength.height} onChange={(e) => setCanvasLength((p) => ({ ...p, height: e.target.value }))} />
                </div>
                <button className="de-btn de-btn-primary" style={{ width: "100%" }} onClick={() => { onSetHeightWidthOfCanva(canvasLength); setSizeAnchorEl(null); }}>
                  Apply
                </button>
              </div>
            </Popover>
          </div>

          <div className="de-topbar-right">
            {isMobile && (
              <button
                type="button"
                className={`de-btn de-btn-icon de-mobile-panel-btn${mobileRightOpen ? " active" : ""}`}
                onClick={toggleMobileRight}
                title="Properties panel"
                aria-label="Toggle properties panel"
              >
                <PanelRight size={16} />
              </button>
            )}
            {canEdit && isEditMode && (
              <button className="de-btn" onClick={onStartNew}>
                <Plus size={14} /> New
              </button>
            )}
            <button className="de-btn" onClick={onExportPng}>Export</button>
            {canEdit && (
              <>
                <button className="de-btn de-btn-icon de-btn-ghost" onClick={onClearResources} title="Clear canvas">
                  <Trash2 size={16} />
                </button>
                <button className="de-btn de-btn-primary" onClick={isEditMode ? onEditTemplate : onSaveTemplate}>
                  <Save size={14} /> {isEditMode ? "Update" : "Save"}
                </button>
              </>
            )}
          </div>
        </header>

        {/* ── Main Area ── */}
        <div className="de-main">
          {/* Left Sidebar */}
          <aside
            className={`de-left-sidebar${isMobile && mobileLeftOpen ? " de-left-sidebar--open" : ""}${isMobile && !mobileLeftOpen ? " de-left-sidebar--collapsed" : ""}`}
          >
            <div className="de-ls-header">Elements</div>
            <div className="de-ls-tabs">
              <button className={`de-ls-tab ${leftTab === "register" ? "active" : ""}`} onClick={() => setLeftTab("register")}>Register</button>
              <button className={`de-ls-tab ${leftTab === "media" ? "active" : ""}`} onClick={() => setLeftTab("media")}>Media</button>
              <button className={`de-ls-tab ${leftTab === "playlists" ? "active" : ""}`} onClick={() => setLeftTab("playlists")}>Playlists</button>
              <button className={`de-ls-tab ${leftTab === "text" ? "active" : ""}`} onClick={() => setLeftTab("text")}>Text</button>
              <button className={`de-ls-tab ${leftTab === "apps" ? "active" : ""}`} onClick={() => setLeftTab("apps")}>Apps</button>
            </div>
            <div className="de-ls-search">
              <div className="de-ls-search-wrap">
                <Search size={15} />
                <input
                  type="text"
                  placeholder={
                    leftTab === "register"
                      ? "Search register..."
                      : leftTab === "playlists"
                        ? "Search playlists..."
                        : leftTab === "text"
                          ? "Search text tools..."
                          : leftTab === "apps"
                            ? "Search apps..."
                            : "Search media..."
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="de-ls-content">
              {renderLeftContent()}
            </div>
          </aside>

          {/* Canvas Area */}
          <main
            className="de-canvas-area"
            id="outer-container"
            ref={containerRef}
          >
            <video id="video" style={{ display: "none" }} height="260" width="380" />
            <canvas ref={canvasRef} id="canvas" />
            {showMenu && (
              <div
                className="de-context-menu"
                style={{ top: menuPosition.top, left: menuPosition.left }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <button className="danger" onClick={() => {
                  if (canvasRef.current && activeObj?.id != null) {
                    const removedType = activeObj.eleType;
                    setLayers((prev) =>
                      prev.filter((l) => l.id !== activeObj.id)
                    );
                    canvasRef.current.remove(activeObj);
                    canvasRef.current.discardActiveObject();
                    canvasRef.current.requestRenderAll();
                    setActiveObj({});
                    setShowMenu(false);
                    if (removedType === "directory-register") {
                      setType("Design");
                      setRegisterProps(null);
                      setObjectKey("default");
                    }
                  }
                }}>
                  <Trash2 size={14} /> Remove
                </button>
                <button onClick={() => {
                  if (canvasRef.current) {
                    const act = canvasRef.current.getActiveObject();
                    if (act && typeof act.bringToFront === "function") {
                      canvasRef.current.bringToFront(act);
                      canvasRef.current.renderAll();
                      setActiveObj({});
                      setShowMenu(false);
                    }
                  }
                }}>
                  <ChevronUp size={14} /> Bring Front
                </button>
                <button onClick={() => {
                  if (canvasRef.current) {
                    const acts = canvasRef.current.getActiveObject();
                    if (acts) {
                      canvasRef.current.sendToBack(acts);
                      canvasRef.current.requestRenderAll();
                      setActiveObj({});
                      setShowMenu(false);
                    }
                  }
                }}>
                  <ChevronDown size={14} /> Send Back
                </button>
              </div>
            )}
          </main>

          {/* Right Sidebar */}
          <aside
            className={`de-right-sidebar${isMobile && mobileRightOpen ? " de-right-sidebar--open" : ""}${isMobile && !mobileRightOpen ? " de-right-sidebar--collapsed" : ""}`}
          >
            {renderRightSidebar()}
          </aside>
        </div>

      </div>
  );
}
